/**
 * Google Ads API v22 Client
 * Direct REST API implementation for campaign creation and management
 * 
 * Documentation: https://developers.google.com/google-ads/api/docs/concepts/overview
 * API Version: v22 (Released: 2025-10-15)
 */

import { googleAdsAuth } from './googleAdsAuth';
import { CampaignDraft, GoogleAdsCampaign, KeywordIdea } from '../types/googleAds';

const GOOGLE_ADS_API_VERSION = 'v22';
const GOOGLE_ADS_API_BASE = `https://googleads.googleapis.com/${GOOGLE_ADS_API_VERSION}`;
const DEVELOPER_TOKEN = import.meta.env.VITE_GOOGLE_ADS_DEVELOPER_TOKEN || '';
const MCC_CUSTOMER_ID = import.meta.env.VITE_GOOGLE_ADS_MCC_CUSTOMER_ID || ''; // MCC Manager Account ID

/**
 * Helper to format dates for Google Ads API (YYYYMMDD)
 */
function formatDateForAPI(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
}

/**
 * Helper to convert amount to micros (required by Google Ads API)
 */
function amountToMicros(amount: number): number {
  return Math.round(amount * 1000000);
}

/**
 * Helper to convert micros to amount
 */
function microsToAmount(micros: number): number {
  return micros / 1000000;
}

/**
 * Delay utility for retry logic
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Check if error is a concurrent modification error
 * API v22: CONCURRENT_MODIFICATION_ERROR occurs when multiple mutations
 * target the same object simultaneously
 */
function isConcurrentModificationError(error: any): boolean {
  const errorMessage = error?.message || '';
  return errorMessage.includes('CONCURRENT_MODIFICATION') ||
         errorMessage.includes('DatabaseError.CONCURRENT_MODIFICATION_ERROR') ||
         errorMessage.includes('concurrent_modification');
}

/**
 * Make authenticated API request to Google Ads
 * 
 * IMPORTANT: For MCC (Manager Account) operations:
 * - login-customer-id: Must be the MCC ID (the manager account making the request)
 * - The URL contains the client customer ID (the account being operated on)
 * 
 * This allows managing multiple client accounts from a single MCC.
 * 
 * API v22 COMPLIANCE: 
 * - Added validate_only and partial_failure support
 * - Added retry logic for CONCURRENT_MODIFICATION_ERROR
 * See: https://developers.google.com/google-ads/api/docs/concepts/changing-objects
 */
async function makeGoogleAdsRequest(
  orgId: string,
  customerId: string,
  endpoint: string,
  body: any,
  options?: {
    /** When true, validates the request without executing it */
    validateOnly?: boolean;
    /** When true, allows successful operations even if some fail in a batch */
    partialFailure?: boolean;
    /** Number of retry attempts for concurrent modification errors (default: 3) */
    maxRetries?: number;
  }
): Promise<any> {
  const maxRetries = options?.maxRetries ?? 3;
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Get valid access token (from MCC or client OAuth)
      const accessToken = await googleAdsAuth.getAccessToken(orgId);
      
      // Remove dashes from customer ID
      const cleanCustomerId = customerId.replace(/-/g, '');
      
      // Use MCC ID for login-customer-id header (required for managing client accounts)
      // If no MCC configured, fall back to the customer ID (direct account access)
      const cleanMccId = MCC_CUSTOMER_ID ? MCC_CUSTOMER_ID.replace(/-/g, '') : cleanCustomerId;
      
      const url = `${GOOGLE_ADS_API_BASE}/customers/${cleanCustomerId}/${endpoint}`;
      
      // Add validate_only and partial_failure if specified
      const requestBody = { ...body };
      if (options?.validateOnly) {
        requestBody.validate_only = true;
      }
      if (options?.partialFailure) {
        requestBody.partial_failure = true;
      }
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'developer-token': DEVELOPER_TOKEN,
          'login-customer-id': cleanMccId, // ✅ MCC ID for manager account access
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: { message: response.statusText } }));
        throw new Error(error.error?.message || `API Error: ${response.statusText}`);
      }

      return await response.json();
    } catch (error: any) {
      lastError = error;
      
      // Check for concurrent modification error
      if (isConcurrentModificationError(error) && attempt < maxRetries) {
        // Exponential backoff: 100ms, 200ms, 400ms, ...
        const backoffMs = 100 * Math.pow(2, attempt);
        console.warn(`⚠️ Concurrent modification detected, retry ${attempt + 1}/${maxRetries} in ${backoffMs}ms...`);
        await delay(backoffMs);
        continue;
      }
      
      console.error(`Google Ads API Error (${endpoint}):`, error);
      throw error;
    }
  }
  
  // Should not reach here, but just in case
  throw lastError || new Error('Request failed after retries');
}

export const googleAdsApiClient = {
  /**
   * Validate campaign creation without executing
   * 
   * API v22 COMPLIANCE: Uses validate_only parameter to check for policy violations
   * and configuration errors before actual campaign creation.
   * See: https://developers.google.com/google-ads/api/docs/concepts/changing-objects
   * 
   * @returns Array of validation errors, empty if validation passes
   */
  async validateCampaignCreation(
    orgId: string,
    customerId: string,
    draft: CampaignDraft
  ): Promise<{ valid: boolean; errors: string[] }> {
    const errors: string[] = [];
    
    try {
      // 1. Validate Budget
      const budgetConfig = {
        name: `Budget for ${draft.campaignName}`,
        amount_micros: amountToMicros(draft.dailyBudget).toString(),
        delivery_method: 'STANDARD'
      };
      
      await makeGoogleAdsRequest(
        orgId,
        customerId,
        'campaignBudgets:mutate',
        { operations: [{ create: budgetConfig }] },
        { validateOnly: true }
      );
      console.log('✅ Budget validation passed');
      
      // 2. Validate Campaign
      const campaignConfig: any = {
        name: draft.campaignName,
        status: 'PAUSED',
        start_date: formatDateForAPI(draft.startDate)
      };
      
      if (draft.campaignType === 'search') {
        campaignConfig.advertising_channel_type = 'SEARCH';
        campaignConfig.network_settings = {
          target_google_search: true,
          target_search_network: true,
          target_content_network: false
        };
        
        // API v22: Use MAXIMIZE_CONVERSIONS for Target CPA
        if (draft.biddingStrategy === 'target_cpa' && draft.targetCpa) {
          campaignConfig.maximize_conversions = {
            target_cpa_micros: amountToMicros(draft.targetCpa)
          };
          campaignConfig.bidding_strategy_type = 'MAXIMIZE_CONVERSIONS';
        } else if (draft.biddingStrategy === 'target_roas' && draft.targetRoas) {
          campaignConfig.maximize_conversion_value = {
            target_roas: draft.targetRoas
          };
          campaignConfig.bidding_strategy_type = 'MAXIMIZE_CONVERSION_VALUE';
        } else {
          campaignConfig.bidding_strategy_type = 'MAXIMIZE_CONVERSIONS';
        }
      } else {
        campaignConfig.advertising_channel_type = 'PERFORMANCE_MAX';
        campaignConfig.bidding_strategy_type = 'MAXIMIZE_CONVERSION_VALUE';
      }
      
      await makeGoogleAdsRequest(
        orgId,
        customerId,
        'campaigns:mutate',
        { operations: [{ create: campaignConfig }] },
        { validateOnly: true }
      );
      console.log('✅ Campaign validation passed');
      
      // 3. Validate Ad Content (for Search campaigns)
      if (draft.campaignType === 'search' && draft.searchSettings) {
        const headlines = draft.searchSettings.headlines || [];
        const descriptions = draft.searchSettings.descriptions || [];
        
        // Check minimum requirements
        if (headlines.length < 3) {
          errors.push('Mindestens 3 Headlines erforderlich');
        }
        if (descriptions.length < 2) {
          errors.push('Mindestens 2 Descriptions erforderlich');
        }
        
        // Check character limits
        headlines.forEach((h, i) => {
          if (h.length > 30) {
            errors.push(`Headline ${i + 1} überschreitet 30 Zeichen (${h.length})`);
          }
        });
        descriptions.forEach((d, i) => {
          if (d.length > 90) {
            errors.push(`Description ${i + 1} überschreitet 90 Zeichen (${d.length})`);
          }
        });
      }
      
      // 4. Validate PMax Asset Group (for PMax campaigns)
      if (draft.campaignType === 'pmax' && draft.pmaxSettings) {
        const assetGroup = draft.pmaxSettings.assetGroups?.[0];
        if (!assetGroup) {
          errors.push('Mindestens eine Asset Group erforderlich für PMax');
        } else {
          if (!assetGroup.headlines || assetGroup.headlines.length < 3) {
            errors.push('Mindestens 3 Headlines für PMax Asset Group erforderlich');
          }
          if (!assetGroup.descriptions || assetGroup.descriptions.length < 2) {
            errors.push('Mindestens 2 Descriptions für PMax Asset Group erforderlich');
          }
          if (!assetGroup.images || assetGroup.images.length < 1) {
            errors.push('Mindestens 1 Bild für PMax Asset Group erforderlich');
          }
        }
        
        // Check conversion goals for PMax
        if (!draft.pmaxSettings.conversionGoals || draft.pmaxSettings.conversionGoals.length === 0) {
          errors.push('Conversion Goals sind für PMax erforderlich');
        }
      }
      
      return { valid: errors.length === 0, errors };
      
    } catch (error: any) {
      // Parse Google Ads API errors
      const errorMessage = error.message || 'Unbekannter Validierungsfehler';
      
      // Extract specific policy violations
      if (errorMessage.includes('POLICY_VIOLATION')) {
        errors.push(`Policy-Verletzung: ${errorMessage}`);
      } else if (errorMessage.includes('FIELD_ERROR')) {
        errors.push(`Feldvalidierungsfehler: ${errorMessage}`);
      } else if (errorMessage.includes('MUTATE_ERROR')) {
        errors.push(`Mutate-Fehler: ${errorMessage}`);
      } else {
        errors.push(errorMessage);
      }
      
      return { valid: false, errors };
    }
  },

  /**
   * Step 1: Create Campaign Budget
   */
  async createBudget(
    orgId: string,
    customerId: string,
    budgetName: string,
    dailyBudgetAmount: number
  ): Promise<string> {
    const response = await makeGoogleAdsRequest(
      orgId,
      customerId,
      'campaignBudgets:mutate',
      {
        operations: [{
          create: {
            name: budgetName,
            amount_micros: amountToMicros(dailyBudgetAmount),
            delivery_method: 'STANDARD',
            explicitly_shared: false
          }
        }]
      }
    );

    const budgetResourceName = response.results[0].resource_name;
    console.log(`✅ Budget created: ${budgetResourceName}`);
    return budgetResourceName;
  },

  /**
   * Step 2: Create Search Campaign
   */
  async createCampaign(
    orgId: string,
    customerId: string,
    campaignData: {
      name: string;
      budgetResourceName: string;
      startDate: Date;
      endDate?: Date;
      biddingStrategy: string;
      targetCpa?: number;
      targetRoas?: number;
      maxCpcBidLimit?: number;
    }
  ): Promise<string> {
    const campaignConfig: any = {
      name: campaignData.name,
      advertising_channel_type: 'SEARCH',
      status: 'PAUSED', // Start paused for safety
      campaign_budget: campaignData.budgetResourceName,
      network_settings: {
        target_google_search: true,
        target_search_network: true,
        target_content_network: false,
        target_partner_search_network: false
      },
      start_date: formatDateForAPI(campaignData.startDate),
      geo_target_type_setting: {
        positive_geo_target_type: 'PRESENCE_OR_INTEREST'
      }
    };

    // Add bidding strategy
    // API v22 COMPLIANCE: TARGET_CPA and TARGET_ROAS are Portfolio-only strategies.
    // For Standard strategies, use MaximizeConversions with target_cpa_micros
    // or MaximizeConversionValue with target_roas.
    // See: https://developers.google.com/google-ads/api/docs/campaigns/bidding/strategy-types
    
    if (campaignData.biddingStrategy === 'target_cpa' && campaignData.targetCpa) {
      // Standard Target CPA: Use MAXIMIZE_CONVERSIONS with target_cpa_micros
      campaignConfig.maximize_conversions = {
        target_cpa_micros: amountToMicros(campaignData.targetCpa)
      };
      campaignConfig.bidding_strategy_type = 'MAXIMIZE_CONVERSIONS';
    } else if (campaignData.biddingStrategy === 'target_roas') {
      if (!campaignData.targetRoas || campaignData.targetRoas <= 0) {
        throw new Error('Target ROAS value is required for Target ROAS bidding strategy');
      }
      // Standard Target ROAS: Use MAXIMIZE_CONVERSION_VALUE with target_roas
      campaignConfig.maximize_conversion_value = {
        target_roas: campaignData.targetRoas
      };
      campaignConfig.bidding_strategy_type = 'MAXIMIZE_CONVERSION_VALUE';
    } else if (campaignData.biddingStrategy === 'maximize_conversions') {
      // Maximize Conversions without target CPA
      campaignConfig.maximize_conversions = {};
      campaignConfig.bidding_strategy_type = 'MAXIMIZE_CONVERSIONS';
    } else if (campaignData.biddingStrategy === 'maximize_clicks') {
      campaignConfig.maximize_clicks = {
        cpc_bid_ceiling_micros: (campaignData as any).maxCpcBidLimit 
          ? amountToMicros((campaignData as any).maxCpcBidLimit)
          : undefined
      };
      campaignConfig.bidding_strategy_type = 'MAXIMIZE_CLICKS';
    } else {
      campaignConfig.manual_cpc = {
        enhanced_cpc_enabled: false // Can be enabled for better performance
      };
      campaignConfig.bidding_strategy_type = 'MANUAL_CPC';
    }

    // Add end date if provided
    if (campaignData.endDate) {
      campaignConfig.end_date = formatDateForAPI(campaignData.endDate);
    }

    const response = await makeGoogleAdsRequest(
      orgId,
      customerId,
      'campaigns:mutate',
      {
        operations: [{
          create: campaignConfig
        }]
      }
    );

    const campaignResourceName = response.results[0].resource_name;
    console.log(`✅ Campaign created: ${campaignResourceName}`);
    return campaignResourceName;
  },

  /**
   * Step 3: Add Location Targeting
   * 
   * Supports both location names (via fallback map) and dynamic lookup.
   * For production, consider using searchGeoTargetConstants() for dynamic lookup.
   */
  async addLocationTargeting(
    orgId: string,
    customerId: string,
    campaignResourceName: string,
    locations: string[]
  ): Promise<void> {
    // Fallback location ID mapping (common ones)
    // For dynamic lookup, use searchGeoTargetConstants()
    const locationMap: Record<string, string> = {
      'United States': '2840',
      'Germany': '2276',
      'United Kingdom': '2826',
      'Austria': '2040',
      'Switzerland': '2756',
      'Canada': '2124',
      'France': '2250',
      'Spain': '2724',
      'Italy': '2380',
      'Netherlands': '2528',
      'Belgium': '2056',
      'Poland': '2616',
      'Sweden': '2752',
      'Norway': '2578',
      'Denmark': '2208',
      'Finland': '2246',
      'Australia': '2036',
      'New Zealand': '2554',
      'Japan': '2392',
      'Brazil': '2076',
      'Mexico': '2484'
    };

    const operations = locations.map(location => ({
      create: {
        campaign: campaignResourceName,
        location: {
          geo_target_constant: `geoTargetConstants/${locationMap[location] || '2840'}`
        },
        negative: false
      }
    }));

    await makeGoogleAdsRequest(
      orgId,
      customerId,
      'campaignCriteria:mutate',
      { operations }
    );

    console.log(`✅ Location targeting added: ${locations.join(', ')}`);
  },

  /**
   * Step 4: Add Language Targeting
   * 
   * Supports language names via fallback map.
   * For dynamic lookup, use getLanguageConstants()
   */
  async addLanguageTargeting(
    orgId: string,
    customerId: string,
    campaignResourceName: string,
    languages: string[]
  ): Promise<void> {
    // Fallback language ID mapping
    // For dynamic lookup, use getLanguageConstants()
    const languageMap: Record<string, string> = {
      'English': '1000',
      'German': '1001',
      'French': '1002',
      'Spanish': '1003',
      'Italian': '1004',
      'Dutch': '1010',
      'Portuguese': '1014',
      'Polish': '1030',
      'Swedish': '1015',
      'Norwegian': '1013',
      'Danish': '1009',
      'Finnish': '1011',
      'Japanese': '1005',
      'Chinese (Simplified)': '1017',
      'Chinese (Traditional)': '1018',
      'Korean': '1012',
      'Russian': '1031',
      'Arabic': '1019'
    };

    const operations = languages.map(language => ({
      create: {
        campaign: campaignResourceName,
        language: {
          language_constant: `languageConstants/${languageMap[language] || '1000'}`
        }
      }
    }));

    await makeGoogleAdsRequest(
      orgId,
      customerId,
      'campaignCriteria:mutate',
      { operations }
    );

    console.log(`✅ Language targeting added: ${languages.join(', ')}`);
  },

  /**
   * Step 5: Create Ad Group
   */
  async createAdGroup(
    orgId: string,
    customerId: string,
    campaignResourceName: string,
    adGroupName: string,
    cpcBidDollars: number = 2.0
  ): Promise<string> {
    const response = await makeGoogleAdsRequest(
      orgId,
      customerId,
      'adGroups:mutate',
      {
        operations: [{
          create: {
            name: adGroupName,
            campaign: campaignResourceName,
            status: 'ENABLED',
            type: 'SEARCH_STANDARD',
            cpc_bid_micros: amountToMicros(cpcBidDollars)
          }
        }]
      }
    );

    const adGroupResourceName = response.results[0].resource_name;
    console.log(`✅ Ad Group created: ${adGroupResourceName}`);
    return adGroupResourceName;
  },

  /**
   * Step 6: Add Keywords to Ad Group
   */
  async addKeywords(
    orgId: string,
    customerId: string,
    adGroupResourceName: string,
    keywords: Array<{ text: string; matchType: string }>
  ): Promise<void> {
    const operations = keywords.map(keyword => ({
      create: {
        ad_group: adGroupResourceName,
        status: 'ENABLED',
        keyword: {
          text: keyword.text,
          match_type: keyword.matchType.toUpperCase() // EXACT, PHRASE, BROAD
        }
      }
    }));

    // API v22 COMPLIANCE: partial_failure allows successful keywords to be added
    // even if some fail (e.g., duplicate keyword errors)
    await makeGoogleAdsRequest(
      orgId,
      customerId,
      'adGroupCriteria:mutate',
      { operations },
      { partialFailure: true }
    );

    console.log(`✅ ${keywords.length} keywords added`);
  },

  /**
   * Step 6b: Add Negative Keywords (Ad Group Level)
   * API v22 COMPLIANCE: Uses partial_failure for robustness
   */
  async addNegativeKeywords(
    orgId: string,
    customerId: string,
    adGroupResourceName: string,
    negativeKeywords: string[]
  ): Promise<void> {
    if (negativeKeywords.length === 0) return;

    const operations = negativeKeywords.map(keyword => ({
      create: {
        ad_group: adGroupResourceName,
        status: 'ENABLED',
        negative: true,
        keyword: {
          text: keyword,
          match_type: 'BROAD'
        }
      }
    }));

    // API v22 COMPLIANCE: partial_failure allows successful keywords to be added
    // even if some fail (e.g., duplicate keyword errors)
    await makeGoogleAdsRequest(
      orgId,
      customerId,
      'adGroupCriteria:mutate',
      { operations },
      { partialFailure: true }
    );

    console.log(`✅ ${negativeKeywords.length} negative keywords added to ad group`);
  },

  /**
   * Add Campaign-Level Negative Keywords (RMF Required)
   * Applied to entire campaign, not just one ad group
   */
  async addCampaignNegativeKeywords(
    orgId: string,
    customerId: string,
    campaignResourceName: string,
    negativeKeywords: string[],
    matchType: 'BROAD' | 'PHRASE' | 'EXACT' = 'BROAD'
  ): Promise<void> {
    if (negativeKeywords.length === 0) return;

    const operations = negativeKeywords.map(keyword => ({
      create: {
        campaign: campaignResourceName,
        status: 'ENABLED',
        negative: true,
        keyword: {
          text: keyword,
          match_type: matchType
        }
      }
    }));

    await makeGoogleAdsRequest(
      orgId,
      customerId,
      'campaignCriteria:mutate',
      { operations }
    );

    console.log(`✅ ${negativeKeywords.length} campaign-level negative keywords added`);
  },

  /**
   * List Campaign-Level Negative Keywords
   */
  async listCampaignNegativeKeywords(
    orgId: string,
    customerId: string,
    campaignResourceName: string
  ): Promise<any[]> {
    try {
      const accessToken = await googleAdsAuth.getAccessToken(orgId);
      const cleanCustomerId = customerId.replace(/-/g, '');

      const query = `
        SELECT
          campaign_criterion.criterion_id,
          campaign_criterion.keyword.text,
          campaign_criterion.keyword.match_type,
          campaign_criterion.negative,
          campaign_criterion.status
        FROM campaign_criterion
        WHERE campaign_criterion.campaign = '${campaignResourceName}'
          AND campaign_criterion.negative = true
          AND campaign_criterion.type = 'KEYWORD'
          AND campaign_criterion.status != 'REMOVED'
      `;

      const response = await fetch(
        `${GOOGLE_ADS_API_BASE}/customers/${cleanCustomerId}/googleAds:search`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'developer-token': DEVELOPER_TOKEN,
            'login-customer-id': cleanCustomerId,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ query })
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to list campaign negative keywords: ${response.statusText}`);
      }

      const data = await response.json();
      return data.results || [];
    } catch (error) {
      console.error('Error listing campaign negative keywords:', error);
      return [];
    }
  },

  /**
   * Remove Campaign-Level Negative Keyword
   */
  async removeCampaignNegativeKeyword(
    orgId: string,
    customerId: string,
    criterionResourceName: string
  ): Promise<void> {
    await makeGoogleAdsRequest(
      orgId,
      customerId,
      'campaignCriteria:mutate',
      {
        operations: [{
          remove: criterionResourceName
        }]
      }
    );

    console.log(`✅ Campaign negative keyword removed`);
  },

  /**
   * Step 7: Create Responsive Search Ad
   */
  async createResponsiveSearchAd(
    orgId: string,
    customerId: string,
    adGroupResourceName: string,
    adData: {
      headlines: string[];
      descriptions: string[];
      finalUrl: string;
      path1?: string;
      path2?: string;
    }
  ): Promise<void> {
    const response = await makeGoogleAdsRequest(
      orgId,
      customerId,
      'adGroupAds:mutate',
      {
        operations: [{
          create: {
            ad_group: adGroupResourceName,
            status: 'ENABLED',
            ad: {
              final_urls: [adData.finalUrl],
              responsive_search_ad: {
                headlines: adData.headlines.slice(0, 15).map(text => ({ text })),
                descriptions: adData.descriptions.slice(0, 4).map(text => ({ text })),
                path1: adData.path1 || '',
                path2: adData.path2 || ''
              }
            }
          }
        }]
      }
    );

    console.log(`✅ Responsive Search Ad created`);
  },

  /**
   * Create complete Search Campaign (all steps)
   * 
   * API v22 COMPLIANCE: Now includes optional validate_only pre-validation
   */
  async createCompleteSearchCampaign(
    orgId: string,
    customerId: string,
    draft: CampaignDraft,
    onProgress?: (step: string, current: number, total: number) => void,
    options?: { skipValidation?: boolean }
  ): Promise<{
    campaignResourceName: string;
    budgetResourceName: string;
    adGroupResourceName: string;
  }> {
    const totalSteps = options?.skipValidation ? 7 : 8;
    let currentStep = 0;

    try {
      // Step 0: Pre-validate campaign (optional but recommended)
      if (!options?.skipValidation) {
        currentStep++;
        onProgress?.('Validating campaign configuration...', currentStep, totalSteps);
        
        const validation = await this.validateCampaignCreation(orgId, customerId, draft);
        if (!validation.valid) {
          throw new Error(`Validation failed:\n${validation.errors.join('\n')}`);
        }
        console.log('✅ Campaign pre-validation passed');
      }

      // Step 1: Create Budget
      currentStep++;
      onProgress?.('Creating budget...', currentStep, totalSteps);
      const budgetResourceName = await this.createBudget(
        orgId,
        customerId,
        `Budget for ${draft.campaignName}`,
        draft.dailyBudget
      );

      // Step 2: Create Campaign
      currentStep++;
      onProgress?.('Creating campaign...', currentStep, totalSteps);
      const campaignResourceName = await this.createCampaign(
        orgId,
        customerId,
        {
          name: draft.campaignName,
          budgetResourceName,
          startDate: draft.startDate,
          endDate: draft.endDate,
          biddingStrategy: draft.biddingStrategy,
          targetCpa: draft.targetCpa,
          targetRoas: draft.targetRoas,
          maxCpcBidLimit: draft.maxCpcBidLimit
        }
      );

      // Step 3: Add Location Targeting
      currentStep++;
      onProgress?.('Adding location targeting...', currentStep, totalSteps);
      await this.addLocationTargeting(
        orgId,
        customerId,
        campaignResourceName,
        draft.locations
      );

      // Step 4: Add Language Targeting
      currentStep++;
      onProgress?.('Adding language targeting...', currentStep, totalSteps);
      await this.addLanguageTargeting(
        orgId,
        customerId,
        campaignResourceName,
        draft.languages
      );

      // Step 5: Create Ad Group
      currentStep++;
      onProgress?.('Creating ad group...', currentStep, totalSteps);
      const adGroupResourceName = await this.createAdGroup(
        orgId,
        customerId,
        campaignResourceName,
        'Ad Group 1',
        2.0
      );

      // Step 6: Add Keywords
      currentStep++;
      onProgress?.('Adding keywords...', currentStep, totalSteps);
      if (draft.searchSettings?.keywords && draft.searchSettings.keywords.length > 0) {
        await this.addKeywords(
          orgId,
          customerId,
          adGroupResourceName,
          draft.searchSettings.keywords
        );
      }

      // Add negative keywords if provided
      if (draft.searchSettings?.negativeKeywords && draft.searchSettings.negativeKeywords.length > 0) {
        await this.addNegativeKeywords(
          orgId,
          customerId,
          adGroupResourceName,
          draft.searchSettings.negativeKeywords
        );
      }

      // Step 7: Create Responsive Search Ad
      currentStep++;
      onProgress?.('Creating ad...', currentStep, totalSteps);
      if (draft.searchSettings?.headlines && draft.searchSettings.descriptions) {
        await this.createResponsiveSearchAd(
          orgId,
          customerId,
          adGroupResourceName,
          {
            headlines: draft.searchSettings.headlines,
            descriptions: draft.searchSettings.descriptions,
            finalUrl: draft.landingPageUrl,
            path1: 'crm',
            path2: 'software'
          }
        );
      }

      // Step 8: Apply Advanced Targeting (if provided)
      if (draft.advancedTargeting) {
        console.log('🎯 Applying advanced targeting...');

        // Demographics (Age, Gender, Income)
        if (draft.advancedTargeting.demographics) {
          const demo = draft.advancedTargeting.demographics;

          if (demo.ageRanges && demo.ageRanges.length > 0) {
            await this.addAgeCriteria(
              orgId,
              customerId,
              adGroupResourceName,
              demo.ageRanges.map(age => `AGE_RANGE_${age.replace('-', '_')}`)
            );
          }

          if (demo.genders && demo.genders.length > 0) {
            await this.addGenderCriteria(
              orgId,
              customerId,
              adGroupResourceName,
              demo.genders
            );
          }

          if (demo.householdIncomes && demo.householdIncomes.length > 0) {
            await this.addIncomeCriteria(
              orgId,
              customerId,
              adGroupResourceName,
              demo.householdIncomes.map(income => `INCOME_RANGE_${income}`)
            );
          }
        }

        // Audiences
        if (draft.advancedTargeting.audiences && draft.advancedTargeting.audiences.length > 0) {
          const audienceIds = draft.advancedTargeting.audiences.map(a => a.id);
          await this.addAudienceCriteria(
            orgId,
            customerId,
            campaignResourceName,
            audienceIds
          );
        }

        // Ad Schedule (Dayparting) - BATCH OPERATION
        // API v22 COMPLIANCE: Using batch operations to reduce API calls
        if (draft.advancedTargeting.adSchedule && draft.advancedTargeting.adSchedule.length > 0) {
          await this.addAdSchedulesBatch(
            orgId,
            customerId,
            adGroupResourceName,
            draft.advancedTargeting.adSchedule
          );
        }

        // Device Bid Modifiers - BATCH OPERATION
        // API v22 COMPLIANCE: Using batch operations to reduce API calls
        if (draft.advancedTargeting.deviceBidModifiers && draft.advancedTargeting.deviceBidModifiers.length > 0) {
          await this.setDeviceBidModifiersBatch(
            orgId,
            customerId,
            campaignResourceName,
            draft.advancedTargeting.deviceBidModifiers.map(m => ({
              device: m.device,
              bidModifierPercent: m.bidModifier
            }))
          );
        }

        console.log('✅ Advanced targeting applied');
      }

      console.log('🎉 Complete Search Campaign created successfully!');

      return {
        campaignResourceName,
        budgetResourceName,
        adGroupResourceName
      };
    } catch (error) {
      console.error('❌ Error creating campaign:', error);
      throw error;
    }
  },

  /**
   * Get campaign performance data via GAQL query
   */
  async getCampaignPerformance(
    orgId: string,
    customerId: string,
    campaignResourceName: string
  ): Promise<any> {
    try {
      const accessToken = await googleAdsAuth.getAccessToken(orgId);
      const cleanCustomerId = customerId.replace(/-/g, '');
      
      // GAQL Query for campaign performance
      const query = `
        SELECT
          campaign.id,
          campaign.name,
          campaign.status,
          metrics.impressions,
          metrics.clicks,
          metrics.conversions,
          metrics.cost_micros,
          metrics.ctr,
          metrics.average_cpc,
          metrics.conversions_value
        FROM campaign
        WHERE campaign.resource_name = '${campaignResourceName}'
      `;

      const response = await fetch(
        `${GOOGLE_ADS_API_BASE}/customers/${cleanCustomerId}/googleAds:search`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'developer-token': DEVELOPER_TOKEN,
            'login-customer-id': cleanCustomerId,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ query })
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch performance: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.results && data.results.length > 0) {
        const result = data.results[0];
        const metrics = result.metrics;
        
        return {
          impressions: parseInt(metrics.impressions) || 0,
          clicks: parseInt(metrics.clicks) || 0,
          conversions: parseFloat(metrics.conversions) || 0,
          cost: microsToAmount(parseInt(metrics.cost_micros) || 0),
          ctr: parseFloat(metrics.ctr) || 0,
          avgCpc: microsToAmount(parseInt(metrics.average_cpc) || 0),
          conversionValue: parseFloat(metrics.conversions_value) || 0
        };
      }

      return null;
    } catch (error) {
      console.error('Error fetching campaign performance:', error);
      return null;
    }
  },

  /**
   * Update campaign status
   */
  async updateCampaignStatus(
    orgId: string,
    customerId: string,
    campaignResourceName: string,
    status: 'ENABLED' | 'PAUSED'
  ): Promise<void> {
    await makeGoogleAdsRequest(
      orgId,
      customerId,
      'campaigns:mutate',
      {
        operations: [{
          update: {
            resource_name: campaignResourceName,
            status
          },
          update_mask: { paths: ['status'] }
        }]
      }
    );

    console.log(`✅ Campaign status updated to: ${status}`);
  },

  /**
   * Update campaign budget
   */
  async updateCampaignBudget(
    orgId: string,
    customerId: string,
    budgetResourceName: string,
    newDailyBudgetAmount: number
  ): Promise<void> {
    await makeGoogleAdsRequest(
      orgId,
      customerId,
      'campaignBudgets:mutate',
      {
        operations: [{
          update: {
            resource_name: budgetResourceName,
            amount_micros: amountToMicros(newDailyBudgetAmount)
          },
          update_mask: { paths: ['amount_micros'] }
        }]
      }
    );

    console.log(`✅ Budget updated to: ${newDailyBudgetAmount} €/Tag`);
  },

  /**
   * List all campaigns for a customer
   */
  async listCampaigns(
    orgId: string,
    customerId: string
  ): Promise<any[]> {
    try {
      const accessToken = await googleAdsAuth.getAccessToken(orgId);
      const cleanCustomerId = customerId.replace(/-/g, '');
      
      const query = `
        SELECT
          campaign.id,
          campaign.name,
          campaign.status,
          campaign.advertising_channel_type,
          campaign_budget.amount_micros,
          metrics.impressions,
          metrics.clicks,
          metrics.conversions,
          metrics.cost_micros
        FROM campaign
        WHERE campaign.status != 'REMOVED'
        ORDER BY campaign.id DESC
        LIMIT 100
      `;

      const response = await fetch(
        `${GOOGLE_ADS_API_BASE}/customers/${cleanCustomerId}/googleAds:search`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'developer-token': DEVELOPER_TOKEN,
            'login-customer-id': cleanCustomerId,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ query })
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to list campaigns: ${response.statusText}`);
      }

      const data = await response.json();
      return data.results || [];
    } catch (error) {
      console.error('Error listing campaigns:', error);
      return [];
    }
  },

  /**
   * Remove/Delete campaign
   */
  async removeCampaign(
    orgId: string,
    customerId: string,
    campaignResourceName: string
  ): Promise<void> {
    await makeGoogleAdsRequest(
      orgId,
      customerId,
      'campaigns:mutate',
      {
        operations: [{
          remove: campaignResourceName
        }]
      }
    );

    console.log(`✅ Campaign removed: ${campaignResourceName}`);
  },

  // ============================================================================
  // KEYWORD MANAGEMENT
  // ============================================================================

  /**
   * Add keywords to existing Ad Group
   */
  async addKeywordsToExistingAdGroup(
    orgId: string,
    customerId: string,
    adGroupResourceName: string,
    keywords: Array<{ text: string; matchType: string; maxCpc?: number }>
  ): Promise<void> {
    const operations = keywords.map(keyword => ({
      create: {
        ad_group: adGroupResourceName,
        status: 'ENABLED',
        keyword: {
          text: keyword.text,
          match_type: keyword.matchType.toUpperCase()
        },
        ...(keyword.maxCpc ? { cpc_bid_micros: amountToMicros(keyword.maxCpc) } : {})
      }
    }));

    await makeGoogleAdsRequest(
      orgId,
      customerId,
      'adGroupCriteria:mutate',
      { operations }
    );

    console.log(`✅ ${keywords.length} keywords added to ad group`);
  },

  /**
   * Remove keyword
   */
  async removeKeyword(
    orgId: string,
    customerId: string,
    keywordResourceName: string
  ): Promise<void> {
    await makeGoogleAdsRequest(
      orgId,
      customerId,
      'adGroupCriteria:mutate',
      {
        operations: [{
          remove: keywordResourceName
        }]
      }
    );

    console.log(`✅ Keyword removed: ${keywordResourceName}`);
  },

  /**
   * Update keyword bid
   */
  async updateKeywordBid(
    orgId: string,
    customerId: string,
    keywordResourceName: string,
    newCpcAmount: number
  ): Promise<void> {
    await makeGoogleAdsRequest(
      orgId,
      customerId,
      'adGroupCriteria:mutate',
      {
        operations: [{
          update: {
            resource_name: keywordResourceName,
            cpc_bid_micros: amountToMicros(newCpcAmount)
          },
          update_mask: { paths: ['cpc_bid_micros'] }
        }]
      }
    );

    console.log(`✅ Keyword bid updated to ${newCpcAmount} €`);
  },

  /**
   * Update keyword status (RMF Required - Pause/Enable)
   */
  async updateKeywordStatus(
    orgId: string,
    customerId: string,
    keywordResourceName: string,
    status: 'ENABLED' | 'PAUSED'
  ): Promise<void> {
    await makeGoogleAdsRequest(
      orgId,
      customerId,
      'adGroupCriteria:mutate',
      {
        operations: [{
          update: {
            resource_name: keywordResourceName,
            status
          },
          update_mask: { paths: ['status'] }
        }]
      }
    );

    console.log(`✅ Keyword status updated to: ${status}`);
  },

  /**
   * Bulk update keyword status
   */
  async bulkUpdateKeywordStatus(
    orgId: string,
    customerId: string,
    keywordResourceNames: string[],
    status: 'ENABLED' | 'PAUSED'
  ): Promise<void> {
    const operations = keywordResourceNames.map(resourceName => ({
      update: {
        resource_name: resourceName,
        status
      },
      update_mask: { paths: ['status'] }
    }));

    await makeGoogleAdsRequest(
      orgId,
      customerId,
      'adGroupCriteria:mutate',
      { operations }
    );

    console.log(`✅ ${keywordResourceNames.length} keywords ${status === 'ENABLED' ? 'enabled' : 'paused'}`);
  },

  /**
   * Get keyword performance for an ad group
   */
  async getKeywordPerformance(
    orgId: string,
    customerId: string,
    adGroupResourceName: string
  ): Promise<any[]> {
    try {
      const accessToken = await googleAdsAuth.getAccessToken(orgId);
      const cleanCustomerId = customerId.replace(/-/g, '');
      
      const query = `
        SELECT
          ad_group_criterion.criterion_id,
          ad_group_criterion.keyword.text,
          ad_group_criterion.keyword.match_type,
          ad_group_criterion.cpc_bid_micros,
          ad_group_criterion.status,
          metrics.impressions,
          metrics.clicks,
          metrics.ctr,
          metrics.cost_micros,
          metrics.conversions,
          metrics.average_cpc
        FROM keyword_view
        WHERE ad_group_criterion.ad_group = '${adGroupResourceName}'
          AND ad_group_criterion.status != 'REMOVED'
        ORDER BY metrics.clicks DESC
      `;

      const response = await fetch(
        `${GOOGLE_ADS_API_BASE}/customers/${cleanCustomerId}/googleAds:search`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'developer-token': DEVELOPER_TOKEN,
            'login-customer-id': cleanCustomerId,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ query })
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch keyword performance: ${response.statusText}`);
      }

      const data = await response.json();
      return data.results || [];
    } catch (error) {
      console.error('Error fetching keyword performance:', error);
      return [];
    }
  },

  // ============================================================================
  // AD MANAGEMENT
  // ============================================================================

  /**
   * Update ad status (pause/enable)
   */
  async updateAdStatus(
    orgId: string,
    customerId: string,
    adGroupAdResourceName: string,
    status: 'ENABLED' | 'PAUSED'
  ): Promise<void> {
    await makeGoogleAdsRequest(
      orgId,
      customerId,
      'adGroupAds:mutate',
      {
        operations: [{
          update: {
            resource_name: adGroupAdResourceName,
            status
          },
          update_mask: { paths: ['status'] }
        }]
      }
    );

    console.log(`✅ Ad status updated to: ${status}`);
  },

  /**
   * Create new RSA variation in existing ad group
   */
  async createAdVariation(
    orgId: string,
    customerId: string,
    adGroupResourceName: string,
    adData: {
      headlines: string[];
      descriptions: string[];
      finalUrl: string;
      path1?: string;
      path2?: string;
    }
  ): Promise<void> {
    await this.createResponsiveSearchAd(
      orgId,
      customerId,
      adGroupResourceName,
      adData
    );

    console.log(`✅ New ad variation created`);
  },

  /**
   * Get ad performance for an ad group
   */
  async getAdPerformance(
    orgId: string,
    customerId: string,
    adGroupResourceName: string
  ): Promise<any[]> {
    try {
      const accessToken = await googleAdsAuth.getAccessToken(orgId);
      const cleanCustomerId = customerId.replace(/-/g, '');
      
      const query = `
        SELECT
          ad_group_ad.ad.id,
          ad_group_ad.ad.responsive_search_ad.headlines,
          ad_group_ad.ad.responsive_search_ad.descriptions,
          ad_group_ad.status,
          ad_group_ad.ad.final_urls,
          metrics.impressions,
          metrics.clicks,
          metrics.ctr,
          metrics.conversions,
          metrics.cost_micros
        FROM ad_group_ad
        WHERE ad_group_ad.ad_group = '${adGroupResourceName}'
          AND ad_group_ad.status != 'REMOVED'
        ORDER BY metrics.clicks DESC
      `;

      const response = await fetch(
        `${GOOGLE_ADS_API_BASE}/customers/${cleanCustomerId}/googleAds:search`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'developer-token': DEVELOPER_TOKEN,
            'login-customer-id': cleanCustomerId,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ query })
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch ad performance: ${response.statusText}`);
      }

      const data = await response.json();
      return data.results || [];
    } catch (error) {
      console.error('Error fetching ad performance:', error);
      return [];
    }
  },

  // ============================================================================
  // BID & BUDGET OPTIMIZATION
  // ============================================================================

  /**
   * Update bidding strategy for campaign
   * 
   * API v22 COMPLIANCE: TARGET_CPA and TARGET_ROAS are Portfolio-only strategies.
   * For Standard strategies, use MaximizeConversions with target_cpa_micros
   * or MaximizeConversionValue with target_roas.
   * See: https://developers.google.com/google-ads/api/docs/campaigns/bidding/strategy-types
   */
  async updateBiddingStrategy(
    orgId: string,
    customerId: string,
    campaignResourceName: string,
    strategy: {
      type: 'MAXIMIZE_CONVERSIONS' | 'TARGET_CPA' | 'MANUAL_CPC' | 'TARGET_ROAS' | 'MAXIMIZE_CLICKS' | 'MAXIMIZE_CONVERSION_VALUE';
      targetCpa?: number;
      targetRoas?: number;
      maxCpcBidLimit?: number;
    }
  ): Promise<void> {
    const updateData: any = {
      resource_name: campaignResourceName
    };

    const updatePaths: string[] = [];

    // Add specific strategy settings
    // Map legacy TARGET_CPA to API-compliant MAXIMIZE_CONVERSIONS with target
    if ((strategy.type === 'TARGET_CPA' || strategy.type === 'MAXIMIZE_CONVERSIONS') && strategy.targetCpa) {
      updateData.maximize_conversions = {
        target_cpa_micros: amountToMicros(strategy.targetCpa)
      };
      updateData.bidding_strategy_type = 'MAXIMIZE_CONVERSIONS';
      updatePaths.push('maximize_conversions', 'bidding_strategy_type');
    } 
    // Map legacy TARGET_ROAS to API-compliant MAXIMIZE_CONVERSION_VALUE with target
    else if ((strategy.type === 'TARGET_ROAS' || strategy.type === 'MAXIMIZE_CONVERSION_VALUE') && strategy.targetRoas) {
      updateData.maximize_conversion_value = {
        target_roas: strategy.targetRoas
      };
      updateData.bidding_strategy_type = 'MAXIMIZE_CONVERSION_VALUE';
      updatePaths.push('maximize_conversion_value', 'bidding_strategy_type');
    } else if (strategy.type === 'MAXIMIZE_CLICKS') {
      updateData.maximize_clicks = {
        cpc_bid_ceiling_micros: strategy.maxCpcBidLimit 
          ? amountToMicros(strategy.maxCpcBidLimit)
          : undefined
      };
      updateData.bidding_strategy_type = 'MAXIMIZE_CLICKS';
      updatePaths.push('maximize_clicks', 'bidding_strategy_type');
    } else if (strategy.type === 'MANUAL_CPC') {
      updateData.manual_cpc = {
        enhanced_cpc_enabled: false
      };
      updateData.bidding_strategy_type = 'MANUAL_CPC';
      updatePaths.push('manual_cpc', 'bidding_strategy_type');
    } else if (strategy.type === 'MAXIMIZE_CONVERSIONS') {
      // MAXIMIZE_CONVERSIONS without target CPA
      updateData.maximize_conversions = {};
      updateData.bidding_strategy_type = 'MAXIMIZE_CONVERSIONS';
      updatePaths.push('maximize_conversions', 'bidding_strategy_type');
    }

    await makeGoogleAdsRequest(
      orgId,
      customerId,
      'campaigns:mutate',
      {
        operations: [{
          update: updateData,
          update_mask: { paths: updatePaths }
        }]
      }
    );

    console.log(`✅ Bidding strategy updated to: ${updateData.bidding_strategy_type}`);
  },

  /**
   * Update ad group level CPC bid
   */
  async updateAdGroupBid(
    orgId: string,
    customerId: string,
    adGroupResourceName: string,
    newCpcAmount: number
  ): Promise<void> {
    await makeGoogleAdsRequest(
      orgId,
      customerId,
      'adGroups:mutate',
      {
        operations: [{
          update: {
            resource_name: adGroupResourceName,
            cpc_bid_micros: amountToMicros(newCpcAmount)
          },
          update_mask: { paths: ['cpc_bid_micros'] }
        }]
      }
    );

    console.log(`✅ Ad Group bid updated to: ${newCpcAmount} €`);
  },

  // ============================================================================
  // KEYWORD PLANNER (KeywordPlanIdeaService v22)
  // ============================================================================

  /**
   * Generate keyword ideas using Google Keyword Planner
   * API v22: KeywordPlanIdeaService.GenerateKeywordIdeas
   */
  async generateKeywordIdeas(
    orgId: string,
    customerId: string,
    seedKeywords: string[],
    landingPageUrl: string,
    locations: string[] = ['United States'],
    language: string = 'English'
  ): Promise<KeywordIdea[]> {
    try {
      const accessToken = await googleAdsAuth.getAccessToken(orgId);
      const cleanCustomerId = customerId.replace(/-/g, '');
      
      // Location mapping
      const locationMap: Record<string, string> = {
        'United States': '2840',
        'Germany': '2276',
        'United Kingdom': '2826',
        'Austria': '2040',
        'Switzerland': '2756'
      };
      
      // Language mapping
      const languageMap: Record<string, string> = {
        'English': '1000',
        'German': '1001',
        'French': '1002',
        'Spanish': '1003'
      };
      
      const requestBody: any = {
        language: `languageConstants/${languageMap[language] || '1000'}`,
        geo_target_constants: locations.map(loc => 
          `geoTargetConstants/${locationMap[loc] || '2840'}`
        ),
        include_adult_keywords: false
      };
      
      // Add keyword seed
      if (seedKeywords && seedKeywords.length > 0) {
        requestBody.keyword_seed = {
          keywords: seedKeywords
        };
      }
      
      // Add URL seed
      if (landingPageUrl) {
        requestBody.url_seed = {
          url: landingPageUrl
        };
      }
      
      const response = await fetch(
        `${GOOGLE_ADS_API_BASE}/customers/${cleanCustomerId}/keywordPlanIdeas:generateKeywordIdeas`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'developer-token': DEVELOPER_TOKEN,
            'login-customer-id': cleanCustomerId,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: { message: response.statusText } }));
        throw new Error(error.error?.message || `Keyword Planner API Error: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Parse results
      const keywordIdeas: KeywordIdea[] = (data.results || []).map((result: any) => {
        const metrics = result.keyword_idea_metrics || {};
        
        return {
          text: result.text || '',
          monthlySearches: parseInt(metrics.avg_monthly_searches) || 0,
          competition: metrics.competition || 'MEDIUM',
          competitionIndex: parseInt(metrics.competition_index) || 50,
          lowBidMicros: parseInt(metrics.low_top_of_page_bid_micros) || 0,
          highBidMicros: parseInt(metrics.high_top_of_page_bid_micros) || 0,
          avgBidMicros: Math.round(
            (parseInt(metrics.low_top_of_page_bid_micros) + parseInt(metrics.high_top_of_page_bid_micros)) / 2
          ) || 0,
          lowBid: microsToAmount(parseInt(metrics.low_top_of_page_bid_micros) || 0),
          highBid: microsToAmount(parseInt(metrics.high_top_of_page_bid_micros) || 0),
          suggestedBid: microsToAmount(
            Math.round(
              (parseInt(metrics.low_top_of_page_bid_micros) + parseInt(metrics.high_top_of_page_bid_micros)) / 2
            ) || 0
          )
        };
      });
      
      console.log(`✅ Generated ${keywordIdeas.length} keyword ideas from Google Keyword Planner`);
      return keywordIdeas;
      
    } catch (error) {
      console.error('Error generating keyword ideas:', error);
      throw error;
    }
  },

  // ============================================================================
  // RECOMMENDATIONS SERVICE (Auto Mode Optimization)
  // ============================================================================

  /**
   * Generate recommendations from Google Ads
   * API v22: RecommendationService.GenerateRecommendations
   */
  async generateRecommendations(
    orgId: string,
    customerId: string,
    request: {
      advertisingChannelType: 'SEARCH' | 'PERFORMANCE_MAX';
      seedKeywords?: string[];
      budgetInfo?: { dailyBudget: number };
    }
  ): Promise<any> {
    try {
      const accessToken = await googleAdsAuth.getAccessToken(orgId);
      const cleanCustomerId = customerId.replace(/-/g, '');
      
      const requestBody: any = {
        customer_id: cleanCustomerId,
        recommendation_types: [
          'CAMPAIGN_BUDGET',
          'KEYWORD',
          'MAXIMIZE_CONVERSIONS_OPT_IN'
        ],
        advertising_channel_type: request.advertisingChannelType
      };

      if (request.seedKeywords && request.seedKeywords.length > 0) {
        requestBody.seed_info = {
          seed_keywords: request.seedKeywords
        };
      }

      const response = await fetch(
        `${GOOGLE_ADS_API_BASE}/customers/${cleanCustomerId}/recommendations:generate`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'developer-token': DEVELOPER_TOKEN,
            'login-customer-id': cleanCustomerId,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        }
      );

      if (!response.ok) {
        throw new Error(`Recommendations API Error: ${response.statusText}`);
      }

      const data = await response.json();
      console.log(`✅ Generated ${data.recommendations?.length || 0} recommendations`);
      return data;
    } catch (error) {
      console.error('Error generating recommendations:', error);
      throw error;
    }
  },

  /**
   * Generate ad group themes from keywords
   * API v22: KeywordPlanIdeaService.GenerateAdGroupThemes
   */
  async generateAdGroupThemes(
    orgId: string,
    customerId: string,
    keywords: string[],
    finalUrl: string
  ): Promise<any[]> {
    try {
      const accessToken = await googleAdsAuth.getAccessToken(orgId);
      const cleanCustomerId = customerId.replace(/-/g, '');
      
      const response = await fetch(
        `${GOOGLE_ADS_API_BASE}/customers/${cleanCustomerId}/keywordPlanIdeas:generateAdGroupThemes`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'developer-token': DEVELOPER_TOKEN,
            'login-customer-id': cleanCustomerId,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            customer_id: cleanCustomerId,
            keywords: keywords,
            final_url: finalUrl
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Ad Group Themes API Error: ${response.statusText}`);
      }

      const data = await response.json();
      console.log(`✅ Generated ${data.ad_group_keyword_suggestions?.length || 0} ad group themes`);
      return data.ad_group_keyword_suggestions || [];
    } catch (error) {
      console.error('Error generating ad group themes:', error);
      throw error;
    }
  },

  /**
   * Generate forecast metrics for campaign
   * API v22: KeywordPlanIdeaService.GenerateForecastMetrics
   */
  async generateForecastMetrics(
    orgId: string,
    customerId: string,
    campaignData: {
      keywords: Array<{ text: string; matchType: string }>;
      cpcBidMicros: number;
      geoTargets: string[];
    }
  ): Promise<any> {
    try {
      const accessToken = await googleAdsAuth.getAccessToken(orgId);
      const cleanCustomerId = customerId.replace(/-/g, '');
      
      const response = await fetch(
        `${GOOGLE_ADS_API_BASE}/customers/${cleanCustomerId}/keywordPlanIdeas:generateForecastMetrics`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'developer-token': DEVELOPER_TOKEN,
            'login-customer-id': cleanCustomerId,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            customer_id: cleanCustomerId,
            campaign: {
              keyword_plan_network: 'GOOGLE_SEARCH',
              geo_target_constants: campaignData.geoTargets,
              cpc_bid_micros: campaignData.cpcBidMicros
            },
            forecast_period: {
              date_range: 'NEXT_MONTH'
            }
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Forecast Metrics API Error: ${response.statusText}`);
      }

      const data = await response.json();
      console.log(`✅ Generated forecast metrics`);
      return data;
    } catch (error) {
      console.error('Error generating forecast metrics:', error);
      throw error;
    }
  },

  // ============================================================================
  // PERFORMANCE MAX - ASSET & ASSET GROUP MANAGEMENT
  // ============================================================================

  /**
   * Create Text Asset (for Headlines, Descriptions)
   */
  async createTextAsset(
    orgId: string,
    customerId: string,
    text: string,
    assetType: 'HEADLINE' | 'DESCRIPTION' | 'LONG_HEADLINE'
  ): Promise<string> {
    const response = await makeGoogleAdsRequest(
      orgId,
      customerId,
      'assets:mutate',
      {
        operations: [{
          create: {
            type: 'TEXT',
            text_asset: {
              text: text
            },
            name: `${assetType}: ${text.substring(0, 30)}`
          }
        }]
      }
    );

    return response.results[0].resource_name;
  },

  /**
   * Create Image Asset
   */
  async createImageAsset(
    orgId: string,
    customerId: string,
    imageUrl: string,
    assetName: string
  ): Promise<string> {
    const response = await makeGoogleAdsRequest(
      orgId,
      customerId,
      'assets:mutate',
      {
        operations: [{
          create: {
            type: 'IMAGE',
            image_asset: {
              full_size: {
                url: imageUrl
              }
            },
            name: assetName
          }
        }]
      }
    );

    return response.results[0].resource_name;
  },

  /**
   * Create Video Asset (YouTube or uploaded)
   */
  async createVideoAsset(
    orgId: string,
    customerId: string,
    videoData: {
      youtubeVideoId?: string;
      videoUrl?: string;
      assetName: string;
    }
  ): Promise<string> {
    const assetCreate: any = {
      type: 'YOUTUBE_VIDEO',
      name: videoData.assetName
    };

    if (videoData.youtubeVideoId) {
      assetCreate.youtube_video_asset = {
        youtube_video_id: videoData.youtubeVideoId
      };
    }

    const response = await makeGoogleAdsRequest(
      orgId,
      customerId,
      'assets:mutate',
      {
        operations: [{
          create: assetCreate
        }]
      }
    );

    return response.results[0].resource_name;
  },

  /**
   * Create Asset Group for PMax
   */
  async createAssetGroup(
    orgId: string,
    customerId: string,
    campaignResourceName: string,
    assetGroupData: {
      name: string;
      finalUrls: string[];
      headlines: string[];
      longHeadlines: string[];
      descriptions: string[];
      businessName: string;
      imageUrls: string[];
      videos?: Array<{ youtubeId?: string; url?: string }>;
      logoUrl?: string;
    }
  ): Promise<string> {
    // Step 1: Create all assets first
    console.log('Creating assets...');
    
    const headlineAssets = await Promise.all(
      assetGroupData.headlines.map(h => 
        this.createTextAsset(orgId, customerId, h, 'HEADLINE')
      )
    );

    const longHeadlineAssets = await Promise.all(
      assetGroupData.longHeadlines.map(h => 
        this.createTextAsset(orgId, customerId, h, 'LONG_HEADLINE')
      )
    );

    const descriptionAssets = await Promise.all(
      assetGroupData.descriptions.map(d => 
        this.createTextAsset(orgId, customerId, d, 'DESCRIPTION')
      )
    );

    const businessNameAsset = await this.createTextAsset(
      orgId, customerId, assetGroupData.businessName, 'HEADLINE'
    );

    // Register all image assets
    const imageAssets = await Promise.all(
      assetGroupData.imageUrls.map((url, idx) =>
        this.createImageAsset(orgId, customerId, url, `Marketing Image ${idx + 1}`)
      )
    );

    // Register video assets (if any)
    const videoAssets: string[] = [];
    if (assetGroupData.videos && assetGroupData.videos.length > 0) {
      console.log(`Registering ${assetGroupData.videos.length} video assets...`);
      for (let i = 0; i < assetGroupData.videos.length; i++) {
        const video = assetGroupData.videos[i];
        try {
          const videoAsset = await this.createVideoAsset(
            orgId,
            customerId,
            {
              youtubeVideoId: video.youtubeId,
              videoUrl: video.url,
              assetName: `Video ${i + 1}`
            }
          );
          videoAssets.push(videoAsset);
        } catch (error) {
          console.warn(`Failed to register video ${i + 1}:`, error);
        }
      }
    }

    // Register logo asset (if provided)
    let logoAsset: string | undefined;
    if (assetGroupData.logoUrl) {
      console.log('Registering logo asset...');
      try {
        logoAsset = await this.createImageAsset(
          orgId,
          customerId,
          assetGroupData.logoUrl,
          'Business Logo'
        );
      } catch (error) {
        console.warn('Failed to register logo:', error);
      }
    }

    // Step 2: Create Asset Group
    const response = await makeGoogleAdsRequest(
      orgId,
      customerId,
      'assetGroups:mutate',
      {
        operations: [{
          create: {
            name: assetGroupData.name,
            campaign: campaignResourceName,
            final_urls: assetGroupData.finalUrls,
            status: 'ENABLED'
          }
        }]
      }
    );

    const assetGroupResourceName = response.results[0].resource_name;

    // Step 3: Link Assets to Asset Group
    const linkOperations: any[] = [];

    headlineAssets.forEach(asset => {
      linkOperations.push({
        create: {
          asset_group: assetGroupResourceName,
          asset: asset,
          field_type: 'HEADLINE'
        }
      });
    });

    longHeadlineAssets.forEach(asset => {
      linkOperations.push({
        create: {
          asset_group: assetGroupResourceName,
          asset: asset,
          field_type: 'LONG_HEADLINE'
        }
      });
    });

    descriptionAssets.forEach(asset => {
      linkOperations.push({
        create: {
          asset_group: assetGroupResourceName,
          asset: asset,
          field_type: 'DESCRIPTION'
        }
      });
    });

    linkOperations.push({
      create: {
        asset_group: assetGroupResourceName,
        asset: businessNameAsset,
        field_type: 'BUSINESS_NAME'
      }
    });

    imageAssets.forEach(asset => {
      linkOperations.push({
        create: {
          asset_group: assetGroupResourceName,
          asset: asset,
          field_type: 'MARKETING_IMAGE'
        }
      });
    });

    // Link video assets (if any)
    videoAssets.forEach(asset => {
      linkOperations.push({
        create: {
          asset_group: assetGroupResourceName,
          asset: asset,
          field_type: 'YOUTUBE_VIDEO'
        }
      });
    });

    // Link logo asset (if provided)
    if (logoAsset) {
      linkOperations.push({
        create: {
          asset_group: assetGroupResourceName,
          asset: logoAsset,
          field_type: 'LOGO'
        }
      });
    }

    // API v22 COMPLIANCE: partial_failure allows successful asset links
    // even if some fail (e.g., invalid video URL)
    await makeGoogleAdsRequest(
      orgId,
      customerId,
      'assetGroupAssets:mutate',
      { operations: linkOperations },
      { partialFailure: true }
    );

    console.log(`✅ Asset Group created with ${linkOperations.length} assets (${imageAssets.length} images, ${videoAssets.length} videos${logoAsset ? ', 1 logo' : ''})`);
    return assetGroupResourceName;
  },

  /**
   * Add Audience Signals to Asset Group
   * 
   * API v22 COMPLIANCE: Asset Group Signals are required for PMax optimization.
   * Signals help Google's AI understand who the target audience is.
   * See: https://developers.google.com/google-ads/api/performance-max/asset-groups#signals
   */
  async addAssetGroupSignals(
    orgId: string,
    customerId: string,
    assetGroupResourceName: string,
    audienceSignals: Array<{
      type: 'USER_INTEREST' | 'USER_LIST' | 'CUSTOM_AUDIENCE' | 'DETAILED_DEMOGRAPHIC';
      id: string;
    }>
  ): Promise<void> {
    if (!audienceSignals || audienceSignals.length === 0) {
      console.log('ℹ️ No audience signals provided, skipping...');
      return;
    }

    const signalOperations: any[] = [];

    for (const signal of audienceSignals) {
      const signalConfig: any = {
        asset_group: assetGroupResourceName
      };

      // Build audience based on signal type
      switch (signal.type) {
        case 'USER_INTEREST':
          // User Interest (Affinity/In-Market audiences)
          signalConfig.audience = {
            user_interest: {
              user_interest_category: `customers/${customerId.replace(/-/g, '')}/userInterests/${signal.id}`
            }
          };
          break;

        case 'USER_LIST':
          // Remarketing lists / Customer Match
          signalConfig.audience = {
            user_list: {
              user_list: `customers/${customerId.replace(/-/g, '')}/userLists/${signal.id}`
            }
          };
          break;

        case 'CUSTOM_AUDIENCE':
          // Custom audiences
          signalConfig.audience = {
            custom_audience: {
              custom_audience: `customers/${customerId.replace(/-/g, '')}/customAudiences/${signal.id}`
            }
          };
          break;

        case 'DETAILED_DEMOGRAPHIC':
          // Detailed demographics (e.g., homeowners, parents, education level)
          signalConfig.audience = {
            detailed_demographic: {
              detailed_demographic: `customers/${customerId.replace(/-/g, '')}/detailedDemographics/${signal.id}`
            }
          };
          break;

        default:
          console.warn(`Unknown signal type: ${signal.type}`);
          continue;
      }

      signalOperations.push({ create: signalConfig });
    }

    if (signalOperations.length === 0) {
      return;
    }

    try {
      await makeGoogleAdsRequest(
        orgId,
        customerId,
        'assetGroupSignals:mutate',
        { operations: signalOperations }
      );
      console.log(`✅ Added ${signalOperations.length} audience signals to Asset Group`);
    } catch (error) {
      console.warn('⚠️ Failed to add audience signals (non-critical):', error);
      // Non-critical error - campaign can still function without signals
    }
  },

  /**
   * Create complete PMax campaign
   * 
   * API v22 COMPLIANCE: Now includes optional validate_only pre-validation
   */
  async createCompletePMaxCampaign(
    orgId: string,
    customerId: string,
    draft: CampaignDraft,
    onProgress?: (step: string, current: number, total: number) => void,
    options?: { skipValidation?: boolean }
  ): Promise<{
    campaignResourceName: string;
    budgetResourceName: string;
    assetGroupResourceName: string;
  }> {
    const totalSteps = options?.skipValidation ? 5 : 6;
    let currentStep = 0;

    try {
      // Step 0: Pre-validate campaign (optional but recommended)
      if (!options?.skipValidation) {
        currentStep++;
        onProgress?.('Validating PMax campaign configuration...', currentStep, totalSteps);
        
        const validation = await this.validateCampaignCreation(orgId, customerId, draft);
        if (!validation.valid) {
          throw new Error(`Validation failed:\n${validation.errors.join('\n')}`);
        }
        console.log('✅ PMax campaign pre-validation passed');
      }

      // Step 1: Create Budget
      currentStep++;
      onProgress?.('Creating budget...', currentStep, totalSteps);
      const budgetResourceName = await this.createBudget(
        orgId, customerId,
        `Budget for ${draft.campaignName}`,
        draft.dailyBudget
      );

      // Step 2: Create PMax Campaign
      currentStep++;
      onProgress?.('Creating Performance Max campaign...', currentStep, totalSteps);
      
      // Build campaign config
      const campaignConfig: any = {
        name: draft.campaignName,
        advertising_channel_type: 'PERFORMANCE_MAX',
        status: 'PAUSED',
        campaign_budget: budgetResourceName,
        bidding_strategy_type: 'MAXIMIZE_CONVERSION_VALUE',
        start_date: formatDateForAPI(draft.startDate)
      };
      
      // API v22 COMPLIANCE: Add shopping_setting for Retail PMax campaigns
      // Required for E-Commerce campaigns with Merchant Center product feeds
      // See: https://developers.google.com/google-ads/api/performance-max/retail
      if (draft.pmaxSettings?.shoppingSetting?.merchantId) {
        const shoppingSetting = draft.pmaxSettings.shoppingSetting;
        campaignConfig.shopping_setting = {
          merchant_id: parseInt(shoppingSetting.merchantId.replace(/-/g, ''), 10),
          feed_label: shoppingSetting.feedLabel || 'EN',
          campaign_priority: shoppingSetting.campaignPriority ?? 0,
          enable_local: shoppingSetting.enableLocal ?? false
        };
        console.log('🛒 Retail PMax: Added Merchant Center shopping_setting', {
          merchantId: campaignConfig.shopping_setting.merchant_id,
          feedLabel: campaignConfig.shopping_setting.feed_label
        });
      }
      
      const campaignResponse = await makeGoogleAdsRequest(
        orgId, customerId,
        'campaigns:mutate',
        {
          operations: [{
            create: campaignConfig
          }]
        }
      );

      const campaignResourceName = campaignResponse.results[0].resource_name;

      // Step 3: Add Location Targeting
      currentStep++;
      onProgress?.('Adding targeting...', currentStep, totalSteps);
      await this.addLocationTargeting(orgId, customerId, campaignResourceName, draft.locations);
      await this.addLanguageTargeting(orgId, customerId, campaignResourceName, draft.languages);

      // Step 4: Create Asset Group
      currentStep++;
      onProgress?.('Creating asset group...', currentStep, totalSteps);
      
      if (!draft.pmaxSettings) {
        throw new Error('PMax settings required');
      }

      const assetGroup = draft.pmaxSettings.assetGroups?.[0];
      if (!assetGroup) {
        throw new Error('At least one asset group required');
      }

      const assetGroupResourceName = await this.createAssetGroup(
        orgId, customerId,
        campaignResourceName,
        {
          name: assetGroup.name,
          finalUrls: [draft.landingPageUrl],
          headlines: assetGroup.headlines,
          longHeadlines: assetGroup.longHeadlines,
          descriptions: assetGroup.descriptions,
          businessName: assetGroup.businessName,
          imageUrls: assetGroup.images,
          videos: assetGroup.videos,
          logoUrl: assetGroup.logoUrl
        }
      );

      // Step 4b: Add Audience Signals to Asset Group
      // API v22 COMPLIANCE: Asset Group Signals help Google's AI optimize for the right audience
      // See: https://developers.google.com/google-ads/api/performance-max/asset-groups#signals
      if (draft.advancedTargeting?.audiences && draft.advancedTargeting.audiences.length > 0) {
        console.log('🎯 Adding audience signals to Asset Group...');
        
        // Map audience types to API signal types
        const mapAudienceType = (type: string): 'USER_INTEREST' | 'USER_LIST' | 'CUSTOM_AUDIENCE' | 'DETAILED_DEMOGRAPHIC' => {
          switch (type?.toLowerCase()) {
            case 'in_market':
            case 'affinity':
            case 'user_interest':
              return 'USER_INTEREST';
            case 'remarketing':
            case 'user_list':
              return 'USER_LIST';
            case 'custom':
            case 'custom_audience':
              return 'CUSTOM_AUDIENCE';
            case 'detailed_demographic':
              return 'DETAILED_DEMOGRAPHIC';
            default:
              return 'USER_INTEREST';
          }
        };
        
        // Convert audience data to signal format
        const signals = draft.advancedTargeting.audiences.map(audience => ({
          type: mapAudienceType(audience.type),
          id: audience.id
        }));
        
        await this.addAssetGroupSignals(
          orgId,
          customerId,
          assetGroupResourceName,
          signals
        );
      }

      // Step 5: Apply Advanced Targeting (if provided)
      if (draft.advancedTargeting) {
        console.log('🎯 Applying advanced targeting to PMax...');

        // Note: Demographics for PMax are typically set on Asset Group level
        // Audiences as signals
        if (draft.advancedTargeting.audiences && draft.advancedTargeting.audiences.length > 0) {
          const audienceIds = draft.advancedTargeting.audiences.map(a => a.id);
          await this.addAudienceCriteria(
            orgId,
            customerId,
            campaignResourceName,
            audienceIds
          );
        }

        // Device Bid Modifiers (Campaign Level) - BATCH OPERATION
        // API v22 COMPLIANCE: Using batch operations to reduce API calls
        if (draft.advancedTargeting.deviceBidModifiers && draft.advancedTargeting.deviceBidModifiers.length > 0) {
          await this.setDeviceBidModifiersBatch(
            orgId,
            customerId,
            campaignResourceName,
            draft.advancedTargeting.deviceBidModifiers.map(m => ({
              device: m.device,
              bidModifierPercent: m.bidModifier
            }))
          );
        }

        console.log('✅ Advanced targeting applied to PMax');
      }

      console.log('🎉 Complete PMax Campaign created successfully!');

      return {
        campaignResourceName,
        budgetResourceName,
        assetGroupResourceName
      };
    } catch (error) {
      console.error('❌ Error creating PMax campaign:', error);
      throw error;
    }
  },

  // ============================================================================
  // CONVERSION ACTIONS (Goals & Tracking)
  // ============================================================================

  /**
   * Create Conversion Action
   * API v22: ConversionActionService.MutateConversionActions
   */
  async createConversionAction(
    orgId: string,
    customerId: string,
    conversionData: {
      name: string;
      category: 'PURCHASE' | 'LEAD' | 'SIGNUP' | 'DOWNLOAD' | 'PAGE_VIEW';
      value: number;
    }
  ): Promise<string> {
    try {
      const response = await makeGoogleAdsRequest(
        orgId,
        customerId,
        'conversionActions:mutate',
        {
          operations: [{
            create: {
              name: conversionData.name,
              type: 'WEBPAGE',
              category: conversionData.category,
              status: 'ENABLED',
              conversion_environment: 'WEB', // REQUIRED by API v22: WEB or APP
              value_settings: {
                default_value: conversionData.value,
                always_use_default_value: true, // Use fixed value for ROAS targeting
                currency_code: 'EUR' // Required for value-based conversions
              },
              counting_type: 'ONE_PER_CLICK',
              attribution_model_settings: {
                attribution_model: 'LAST_CLICK'
              },
              click_through_lookback_window_days: 30 // Standard 30-day attribution window
            }
          }]
        }
      );

      const conversionActionResourceName = response.results[0].resource_name;
      console.log(`✅ Conversion Action created: ${conversionData.name} (Value: ${conversionData.value} €)`);
      return conversionActionResourceName;
    } catch (error) {
      console.error('Error creating conversion action:', error);
      throw error;
    }
  },

  /**
   * List existing Conversion Actions
   */
  async listConversionActions(
    orgId: string,
    customerId: string
  ): Promise<any[]> {
    try {
      const accessToken = await googleAdsAuth.getAccessToken(orgId);
      const cleanCustomerId = customerId.replace(/-/g, '');
      
      const query = `
        SELECT
          conversion_action.id,
          conversion_action.name,
          conversion_action.category,
          conversion_action.status,
          conversion_action.type
        FROM conversion_action
        WHERE conversion_action.status = 'ENABLED'
      `;

      const response = await fetch(
        `${GOOGLE_ADS_API_BASE}/customers/${cleanCustomerId}/googleAds:search`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'developer-token': DEVELOPER_TOKEN,
            'login-customer-id': cleanCustomerId,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ query })
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to list conversion actions: ${response.statusText}`);
      }

      const data = await response.json();
      return data.results || [];
    } catch (error) {
      console.error('Error listing conversion actions:', error);
      return [];
    }
  },

  /**
   * Set Campaign Conversion Goals (PMax Required)
   */
  async setCampaignConversionGoals(
    orgId: string,
    customerId: string,
    campaignResourceName: string,
    conversionActionResourceNames: string[]
  ): Promise<void> {
    try {
      await makeGoogleAdsRequest(
        orgId,
        customerId,
        'campaigns:mutate',
        {
          operations: [{
            update: {
              resource_name: campaignResourceName,
              campaign_goal: {
                conversion_actions: conversionActionResourceNames
              }
            },
            update_mask: { paths: ['campaign_goal'] }
          }]
        }
      );

      console.log(`✅ Campaign conversion goals set (${conversionActionResourceNames.length} goals)`);
    } catch (error) {
      console.error('Error setting conversion goals:', error);
      throw error;
    }
  },

  /**
   * ========================================
   * REPORTING & ANALYTICS API
   * ========================================
   * 
   * API v22 SEARCH STRATEGY:
   * ========================
   * 
   * The Google Ads API provides two methods for retrieving data:
   * 
   * 1. SearchStream (googleAds:searchStream)
   *    - Best for: Performance reports, large datasets, bulk exports
   *    - Advantages: More efficient for large data, streaming response
   *    - Use when: Fetching daily/weekly performance metrics, keyword reports
   * 
   * 2. Search with Pagination (googleAds:search)
   *    - Best for: Individual entity lookups, paginated UI displays
   *    - Advantages: Supports page_token for resumable queries
   *    - Use when: Loading entity lists, handling network interruptions
   * 
   * CURRENT IMPLEMENTATION:
   * - executeQuery(): Uses SearchStream for performance reports
   * - executePagedQuery(): Uses Search with pagination for entity lists
   * - getCampaignPerformance(), getAdGroupMetrics(): Use Search for single entity
   * 
   * See: https://developers.google.com/google-ads/api/docs/concepts/retrieving-objects
   */

  /**
   * Execute GAQL Query using SearchStream
   * 
   * BEST FOR: Performance reports, large datasets, bulk data exports
   * NOT RECOMMENDED FOR: Slow networks or when pagination control is needed
   */
  async executeQuery(
    orgId: string,
    customerId: string,
    query: string
  ): Promise<any[]> {
    try {
      const response = await makeGoogleAdsRequest(
        orgId,
        customerId,
        'googleAds:searchStream',
        {
          query
        }
      );

      return response.results || [];
    } catch (error) {
      console.error('Error executing GAQL query:', error);
      throw error;
    }
  },

  /**
   * Execute Paged GAQL Query using Search with pagination
   * 
   * BEST FOR: Entity lists, paginated UI displays, resumable queries
   * ADVANTAGES: Supports page_token for network resilience
   */
  async executePagedQuery(
    orgId: string,
    customerId: string,
    query: string,
    pageSize: number = 10000,
    pageToken?: string
  ): Promise<{
    results: any[];
    nextPageToken?: string;
    totalResultsCount: number;
  }> {
    try {
      const accessToken = await googleAdsAuth.getAccessToken(orgId);
      const cleanCustomerId = customerId.replace(/-/g, '');

      const requestBody: any = {
        query,
        pageSize
      };

      if (pageToken) {
        requestBody.pageToken = pageToken;
      }

      const response = await fetch(
        `${GOOGLE_ADS_API_BASE}/customers/${cleanCustomerId}/googleAds:search`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'developer-token': DEVELOPER_TOKEN,
            'login-customer-id': cleanCustomerId,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: { message: response.statusText } }));
        throw new Error(error.error?.message || `Paged Query Error: ${response.statusText}`);
      }

      const data = await response.json();

      return {
        results: data.results || [],
        nextPageToken: data.nextPageToken,
        totalResultsCount: parseInt(data.totalResultsCount || '0')
      };
    } catch (error) {
      console.error('Error executing paged query:', error);
      throw error;
    }
  },

  /**
   * Execute Query and fetch all pages
   * Automatically handles pagination to retrieve all results
   */
  async executeQueryAllPages(
    orgId: string,
    customerId: string,
    query: string,
    pageSize: number = 10000
  ): Promise<any[]> {
    const allResults: any[] = [];
    let nextPageToken: string | undefined = undefined;

    try {
      do {
        const response = await this.executePagedQuery(
          orgId,
          customerId,
          query,
          pageSize,
          nextPageToken
        );

        allResults.push(...response.results);
        nextPageToken = response.nextPageToken;

        console.log(`📄 Fetched page with ${response.results.length} results. Total so far: ${allResults.length}`);
      } while (nextPageToken);

      console.log(`✅ Query complete. Total results: ${allResults.length}`);
      return allResults;
    } catch (error) {
      console.error('Error executing query with pagination:', error);
      throw error;
    }
  },

  /**
   * ========================================
   * EXTENSIONS API
   * ========================================
   */

  /**
   * Create Sitelink Asset
   */
  async createSitelinkAsset(
    orgId: string,
    customerId: string,
    sitelink: {
      linkText: string;
      description1: string;
      description2: string;
      finalUrls: string[];
    }
  ): Promise<string> {
    try {
      const response = await makeGoogleAdsRequest(
        orgId,
        customerId,
        'assets:mutate',
        {
          operations: [{
            create: {
              type: 'SITELINK',
              sitelink_asset: {
                link_text: sitelink.linkText,
                description1: sitelink.description1,
                description2: sitelink.description2
              },
              final_urls: sitelink.finalUrls
            }
          }]
        }
      );

      const assetResourceName = response.results[0].resource_name;
      console.log(`✅ Sitelink asset created: ${assetResourceName}`);
      return assetResourceName;
    } catch (error) {
      console.error('Error creating sitelink asset:', error);
      throw error;
    }
  },

  /**
   * Create Callout Asset
   */
  async createCalloutAsset(
    orgId: string,
    customerId: string,
    calloutText: string
  ): Promise<string> {
    try {
      const response = await makeGoogleAdsRequest(
        orgId,
        customerId,
        'assets:mutate',
        {
          operations: [{
            create: {
              type: 'CALLOUT',
              callout_asset: {
                callout_text: calloutText
              }
            }
          }]
        }
      );

      const assetResourceName = response.results[0].resource_name;
      console.log(`✅ Callout asset created: ${assetResourceName}`);
      return assetResourceName;
    } catch (error) {
      console.error('Error creating callout asset:', error);
      throw error;
    }
  },

  /**
   * Create Structured Snippet Asset
   */
  async createStructuredSnippetAsset(
    orgId: string,
    customerId: string,
    header: string,
    values: string[]
  ): Promise<string> {
    try {
      const response = await makeGoogleAdsRequest(
        orgId,
        customerId,
        'assets:mutate',
        {
          operations: [{
            create: {
              type: 'STRUCTURED_SNIPPET',
              structured_snippet_asset: {
                header,
                values
              }
            }
          }]
        }
      );

      const assetResourceName = response.results[0].resource_name;
      console.log(`✅ Structured snippet asset created: ${assetResourceName}`);
      return assetResourceName;
    } catch (error) {
      console.error('Error creating structured snippet asset:', error);
      throw error;
    }
  },

  /**
   * Link Asset to Campaign
   */
  async linkAssetToCampaign(
    orgId: string,
    customerId: string,
    campaignResourceName: string,
    assetResourceName: string,
    fieldType: 'SITELINK' | 'CALLOUT' | 'STRUCTURED_SNIPPET'
  ): Promise<void> {
    try {
      await makeGoogleAdsRequest(
        orgId,
        customerId,
        'campaignAssets:mutate',
        {
          operations: [{
            create: {
              campaign: campaignResourceName,
              asset: assetResourceName,
              field_type: fieldType
            }
          }]
        }
      );

      console.log(`✅ Asset linked to campaign: ${fieldType}`);
    } catch (error) {
      console.error('Error linking asset to campaign:', error);
      throw error;
    }
  },

  /**
   * Unlink Asset from Campaign
   */
  async unlinkAssetFromCampaign(
    orgId: string,
    customerId: string,
    campaignAssetResourceName: string
  ): Promise<void> {
    try {
      await makeGoogleAdsRequest(
        orgId,
        customerId,
        'campaignAssets:mutate',
        {
          operations: [{
            remove: campaignAssetResourceName
          }]
        }
      );

      console.log(`✅ Asset unlinked from campaign`);
    } catch (error) {
      console.error('Error unlinking asset from campaign:', error);
      throw error;
    }
  },

  /**
   * Create and Link Multiple Sitelinks to Campaign
   */
  async createAndLinkSitelinks(
    orgId: string,
    customerId: string,
    campaignResourceName: string,
    sitelinks: Array<{
      linkText: string;
      description1: string;
      description2: string;
      finalUrl: string;
    }>
  ): Promise<void> {
    try {
      for (const sitelink of sitelinks) {
        const assetResourceName = await this.createSitelinkAsset(
          orgId,
          customerId,
          {
            linkText: sitelink.linkText,
            description1: sitelink.description1,
            description2: sitelink.description2,
            finalUrls: [sitelink.finalUrl]
          }
        );

        await this.linkAssetToCampaign(
          orgId,
          customerId,
          campaignResourceName,
          assetResourceName,
          'SITELINK'
        );
      }

      console.log(`✅ ${sitelinks.length} sitelinks created and linked`);
    } catch (error) {
      console.error('Error creating and linking sitelinks:', error);
      throw error;
    }
  },

  /**
   * Create and Link Multiple Callouts to Campaign
   */
  async createAndLinkCallouts(
    orgId: string,
    customerId: string,
    campaignResourceName: string,
    callouts: string[]
  ): Promise<void> {
    try {
      for (const calloutText of callouts) {
        const assetResourceName = await this.createCalloutAsset(
          orgId,
          customerId,
          calloutText
        );

        await this.linkAssetToCampaign(
          orgId,
          customerId,
          campaignResourceName,
          assetResourceName,
          'CALLOUT'
        );
      }

      console.log(`✅ ${callouts.length} callouts created and linked`);
    } catch (error) {
      console.error('Error creating and linking callouts:', error);
      throw error;
    }
  },

  /**
   * Create Account-Level Callout (RMF Required)
   * Links callout to account, not individual campaigns
   */
  async createAccountLevelCallout(
    orgId: string,
    customerId: string,
    calloutText: string
  ): Promise<string> {
    try {
      // Step 1: Create Callout Asset
      const assetResourceName = await this.createCalloutAsset(
        orgId,
        customerId,
        calloutText
      );

      // Step 2: Link to Customer (Account-Level)
      await makeGoogleAdsRequest(
        orgId,
        customerId,
        'customerAssets:mutate',
        {
          operations: [{
            create: {
              asset: assetResourceName,
              field_type: 'CALLOUT'
            }
          }]
        }
      );

      console.log(`✅ Account-level callout created: ${calloutText}`);
      return assetResourceName;
    } catch (error) {
      console.error('Error creating account-level callout:', error);
      throw error;
    }
  },

  /**
   * List Account-Level Callouts
   */
  async listAccountLevelCallouts(
    orgId: string,
    customerId: string
  ): Promise<any[]> {
    try {
      const accessToken = await googleAdsAuth.getAccessToken(orgId);
      const cleanCustomerId = customerId.replace(/-/g, '');

      const query = `
        SELECT
          customer_asset.asset,
          customer_asset.field_type,
          asset.callout_asset.callout_text,
          customer_asset.status
        FROM customer_asset
        WHERE customer_asset.field_type = 'CALLOUT'
          AND customer_asset.status = 'ENABLED'
      `;

      const response = await fetch(
        `${GOOGLE_ADS_API_BASE}/customers/${cleanCustomerId}/googleAds:search`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'developer-token': DEVELOPER_TOKEN,
            'login-customer-id': cleanCustomerId,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ query })
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to list account callouts: ${response.statusText}`);
      }

      const data = await response.json();
      return data.results || [];
    } catch (error) {
      console.error('Error listing account callouts:', error);
      return [];
    }
  },

  /**
   * Remove Account-Level Callout
   */
  async removeAccountLevelCallout(
    orgId: string,
    customerId: string,
    customerAssetResourceName: string
  ): Promise<void> {
    try {
      await makeGoogleAdsRequest(
        orgId,
        customerId,
        'customerAssets:mutate',
        {
          operations: [{
            remove: customerAssetResourceName
          }]
        }
      );

      console.log(`✅ Account-level callout removed`);
    } catch (error) {
      console.error('Error removing account callout:', error);
      throw error;
    }
  },

  /**
   * ========================================
   * TARGETING API (Demographics & Audiences)
   * ========================================
   */

  /**
   * Add Age Criteria to Ad Group
   */
  async addAgeCriteria(
    orgId: string,
    customerId: string,
    adGroupResourceName: string,
    ageRanges: string[]
  ): Promise<void> {
    try {
      const operations = ageRanges.map(ageRange => ({
        create: {
          ad_group: adGroupResourceName,
          age_range: {
            type: ageRange // e.g., 'AGE_RANGE_18_24'
          }
        }
      }));

      await makeGoogleAdsRequest(
        orgId,
        customerId,
        'adGroupCriteria:mutate',
        { operations }
      );

      console.log(`✅ Age criteria added: ${ageRanges.length} ranges`);
    } catch (error) {
      console.error('Error adding age criteria:', error);
      throw error;
    }
  },

  /**
   * Add Gender Criteria to Ad Group
   */
  async addGenderCriteria(
    orgId: string,
    customerId: string,
    adGroupResourceName: string,
    genders: string[]
  ): Promise<void> {
    try {
      const operations = genders.map(gender => ({
        create: {
          ad_group: adGroupResourceName,
          gender: {
            type: gender // e.g., 'MALE', 'FEMALE'
          }
        }
      }));

      await makeGoogleAdsRequest(
        orgId,
        customerId,
        'adGroupCriteria:mutate',
        { operations }
      );

      console.log(`✅ Gender criteria added: ${genders.length} genders`);
    } catch (error) {
      console.error('Error adding gender criteria:', error);
      throw error;
    }
  },

  /**
   * Add Income Criteria to Ad Group
   */
  async addIncomeCriteria(
    orgId: string,
    customerId: string,
    adGroupResourceName: string,
    incomeRanges: string[]
  ): Promise<void> {
    try {
      const operations = incomeRanges.map(income => ({
        create: {
          ad_group: adGroupResourceName,
          income_range: {
            type: income // e.g., 'INCOME_RANGE_TOP_10_PERCENT'
          }
        }
      }));

      await makeGoogleAdsRequest(
        orgId,
        customerId,
        'adGroupCriteria:mutate',
        { operations }
      );

      console.log(`✅ Income criteria added: ${incomeRanges.length} ranges`);
    } catch (error) {
      console.error('Error adding income criteria:', error);
      throw error;
    }
  },

  /**
   * Add Audience Criteria to Campaign
   */
  async addAudienceCriteria(
    orgId: string,
    customerId: string,
    campaignResourceName: string,
    audienceIds: string[]
  ): Promise<void> {
    try {
      const operations = audienceIds.map(audienceId => ({
        create: {
          campaign: campaignResourceName,
          user_list: audienceId
        }
      }));

      await makeGoogleAdsRequest(
        orgId,
        customerId,
        'campaignCriteria:mutate',
        { operations }
      );

      console.log(`✅ Audience criteria added: ${audienceIds.length} audiences`);
    } catch (error) {
      console.error('Error adding audience criteria:', error);
      throw error;
    }
  },

  /**
   * ========================================
   * AD SCHEDULE API (Dayparting)
   * ========================================
   */

  /**
   * Add Ad Schedule to Ad Group
   */
  async addAdSchedule(
    orgId: string,
    customerId: string,
    adGroupResourceName: string,
    dayOfWeek: string,
    startHour: number,
    startMinute: number,
    endHour: number,
    endMinute: number,
    bidModifier?: number
  ): Promise<void> {
    try {
      const schedule: any = {
        ad_group: adGroupResourceName,
        ad_schedule: {
          day_of_week: dayOfWeek, // e.g., 'MONDAY'
          start_hour: startHour,
          start_minute: startMinute === 0 ? 'ZERO' : startMinute === 15 ? 'FIFTEEN' : startMinute === 30 ? 'THIRTY' : 'FORTY_FIVE',
          end_hour: endHour,
          end_minute: endMinute === 0 ? 'ZERO' : endMinute === 15 ? 'FIFTEEN' : endMinute === 30 ? 'THIRTY' : 'FORTY_FIVE'
        }
      };

      if (bidModifier !== undefined && bidModifier !== 0) {
        schedule.bid_modifier = 1 + (bidModifier / 100); // e.g., 20% = 1.20
      }

      await makeGoogleAdsRequest(
        orgId,
        customerId,
        'adGroupCriteria:mutate',
        {
          operations: [{
            create: schedule
          }]
        }
      );

      console.log(`✅ Ad schedule added: ${dayOfWeek} ${startHour}:${startMinute} - ${endHour}:${endMinute}`);
    } catch (error) {
      console.error('Error adding ad schedule:', error);
      throw error;
    }
  },

  /**
   * Add Multiple Ad Schedules in a single batch request
   * 
   * API v22 COMPLIANCE: Batch operations reduce API calls and improve quota efficiency
   * See: https://developers.google.com/google-ads/api/docs/concepts/changing-objects
   */
  async addAdSchedulesBatch(
    orgId: string,
    customerId: string,
    adGroupResourceName: string,
    schedules: Array<{
      dayOfWeek: string;
      startHour: number;
      startMinute: number;
      endHour: number;
      endMinute: number;
      bidModifier?: number;
    }>
  ): Promise<void> {
    if (schedules.length === 0) return;

    try {
      const formatMinute = (min: number) => 
        min === 0 ? 'ZERO' : min === 15 ? 'FIFTEEN' : min === 30 ? 'THIRTY' : 'FORTY_FIVE';

      const operations = schedules.map(schedule => {
        const criterionConfig: any = {
          ad_group: adGroupResourceName,
          ad_schedule: {
            day_of_week: schedule.dayOfWeek,
            start_hour: schedule.startHour,
            start_minute: formatMinute(schedule.startMinute),
            end_hour: schedule.endHour,
            end_minute: formatMinute(schedule.endMinute)
          }
        };

        if (schedule.bidModifier !== undefined && schedule.bidModifier !== 0) {
          criterionConfig.bid_modifier = 1 + (schedule.bidModifier / 100);
        }

        return { create: criterionConfig };
      });

      await makeGoogleAdsRequest(
        orgId,
        customerId,
        'adGroupCriteria:mutate',
        { operations },
        { partialFailure: true }
      );

      console.log(`✅ ${schedules.length} ad schedules added in batch`);
    } catch (error) {
      console.error('Error adding ad schedules batch:', error);
      throw error;
    }
  },

  /**
   * Remove Ad Schedule
   */
  async removeAdSchedule(
    orgId: string,
    customerId: string,
    criterionResourceName: string
  ): Promise<void> {
    try {
      await makeGoogleAdsRequest(
        orgId,
        customerId,
        'adGroupCriteria:mutate',
        {
          operations: [{
            remove: criterionResourceName
          }]
        }
      );

      console.log(`✅ Ad schedule removed`);
    } catch (error) {
      console.error('Error removing ad schedule:', error);
      throw error;
    }
  },

  /**
   * ========================================
   * DEVICE TARGETING API
   * ========================================
   */

  /**
   * Set Device Bid Modifier
   */
  async setDeviceBidModifier(
    orgId: string,
    customerId: string,
    campaignResourceName: string,
    device: 'MOBILE' | 'DESKTOP' | 'TABLET',
    bidModifierPercent: number
  ): Promise<void> {
    try {
      // Convert percentage to multiplier (e.g., 20% = 1.20, -20% = 0.80)
      const bidModifier = 1 + (bidModifierPercent / 100);

      await makeGoogleAdsRequest(
        orgId,
        customerId,
        'campaignCriteria:mutate',
        {
          operations: [{
            create: {
              campaign: campaignResourceName,
              device: {
                type: device
              },
              bid_modifier: bidModifier
            }
          }]
        }
      );

      console.log(`✅ Device bid modifier set: ${device} ${bidModifierPercent > 0 ? '+' : ''}${bidModifierPercent}%`);
    } catch (error) {
      console.error('Error setting device bid modifier:', error);
      throw error;
    }
  },

  /**
   * Set Multiple Device Bid Modifiers in a single batch request
   * 
   * API v22 COMPLIANCE: Batch operations reduce API calls and improve quota efficiency
   * See: https://developers.google.com/google-ads/api/docs/concepts/changing-objects
   */
  async setDeviceBidModifiersBatch(
    orgId: string,
    customerId: string,
    campaignResourceName: string,
    deviceModifiers: Array<{
      device: 'MOBILE' | 'DESKTOP' | 'TABLET';
      bidModifierPercent: number;
    }>
  ): Promise<void> {
    // Filter out zero modifiers
    const validModifiers = deviceModifiers.filter(m => m.bidModifierPercent !== 0);
    if (validModifiers.length === 0) return;

    try {
      const operations = validModifiers.map(mod => ({
        create: {
          campaign: campaignResourceName,
          device: {
            type: mod.device
          },
          bid_modifier: 1 + (mod.bidModifierPercent / 100)
        }
      }));

      await makeGoogleAdsRequest(
        orgId,
        customerId,
        'campaignCriteria:mutate',
        { operations },
        { partialFailure: true }
      );

      console.log(`✅ ${validModifiers.length} device bid modifiers set in batch`);
    } catch (error) {
      console.error('Error setting device bid modifiers batch:', error);
      throw error;
    }
  },

  // ============================================================================
  // GEO & LANGUAGE CONSTANTS API (Dynamic Lookup)
  // ============================================================================
  
  /**
   * Search for Geo Target Constants dynamically
   * 
   * API v22 COMPLIANCE: Uses GeoTargetConstantService for dynamic location lookup
   * instead of hardcoded location IDs.
   * See: https://developers.google.com/google-ads/api/docs/concepts/field-service
   * 
   * @param query Search query (e.g., "New York", "Berlin")
   * @param countryCode Optional ISO country code to filter results (e.g., "US", "DE")
   * @returns Array of matching geo target constants with IDs and names
   */
  async searchGeoTargetConstants(
    orgId: string,
    customerId: string,
    query: string,
    countryCode?: string
  ): Promise<Array<{
    id: string;
    name: string;
    canonicalName: string;
    countryCode: string;
    targetType: string;
  }>> {
    try {
      const accessToken = await googleAdsAuth.getAccessToken(orgId);
      const cleanCustomerId = customerId.replace(/-/g, '');
      
      // Build the location names filter
      const locationNames: any = {
        names: [query]
      };
      
      if (countryCode) {
        locationNames.country_code = countryCode;
      }

      const response = await fetch(
        `${GOOGLE_ADS_API_BASE}/geoTargetConstants:suggest`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'developer-token': DEVELOPER_TOKEN,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            locale: 'en',
            location_names: locationNames
          })
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error?.message || 'Failed to search geo targets');
      }

      const data = await response.json();
      
      return (data.geoTargetConstantSuggestions || []).map((suggestion: any) => ({
        id: suggestion.geoTargetConstant?.resourceName?.split('/').pop() || '',
        name: suggestion.geoTargetConstant?.name || '',
        canonicalName: suggestion.geoTargetConstant?.canonicalName || '',
        countryCode: suggestion.geoTargetConstant?.countryCode || '',
        targetType: suggestion.geoTargetConstant?.targetType || ''
      }));
    } catch (error) {
      console.error('Error searching geo target constants:', error);
      throw error;
    }
  },

  /**
   * Get available Language Constants
   * 
   * API v22 COMPLIANCE: Uses LanguageConstantService for dynamic language lookup
   * 
   * @returns Array of language constants with IDs and names
   */
  async getLanguageConstants(
    orgId: string,
    customerId: string
  ): Promise<Array<{
    id: string;
    code: string;
    name: string;
    targetable: boolean;
  }>> {
    try {
      const accessToken = await googleAdsAuth.getAccessToken(orgId);
      const cleanCustomerId = customerId.replace(/-/g, '');
      
      const query = `
        SELECT
          language_constant.resource_name,
          language_constant.id,
          language_constant.code,
          language_constant.name,
          language_constant.targetable
        FROM language_constant
        WHERE language_constant.targetable = TRUE
        ORDER BY language_constant.name
      `;

      const response = await fetch(
        `${GOOGLE_ADS_API_BASE}/customers/${cleanCustomerId}/googleAds:search`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'developer-token': DEVELOPER_TOKEN,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ query })
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error?.message || 'Failed to get language constants');
      }

      const data = await response.json();
      
      return (data.results || []).map((result: any) => ({
        id: result.languageConstant?.id?.toString() || '',
        code: result.languageConstant?.code || '',
        name: result.languageConstant?.name || '',
        targetable: result.languageConstant?.targetable || false
      }));
    } catch (error) {
      console.error('Error getting language constants:', error);
      throw error;
    }
  },

  /**
   * Get Geo Target Constants by IDs
   * 
   * Useful for looking up details of specific geo target IDs
   */
  async getGeoTargetConstantsByIds(
    orgId: string,
    customerId: string,
    geoTargetIds: string[]
  ): Promise<Array<{
    id: string;
    name: string;
    canonicalName: string;
    countryCode: string;
    targetType: string;
  }>> {
    try {
      const accessToken = await googleAdsAuth.getAccessToken(orgId);
      const cleanCustomerId = customerId.replace(/-/g, '');
      
      const resourceNames = geoTargetIds.map(id => `geoTargetConstants/${id}`);
      
      const query = `
        SELECT
          geo_target_constant.resource_name,
          geo_target_constant.id,
          geo_target_constant.name,
          geo_target_constant.canonical_name,
          geo_target_constant.country_code,
          geo_target_constant.target_type
        FROM geo_target_constant
        WHERE geo_target_constant.resource_name IN (${resourceNames.map(r => `'${r}'`).join(', ')})
      `;

      const response = await fetch(
        `${GOOGLE_ADS_API_BASE}/customers/${cleanCustomerId}/googleAds:search`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'developer-token': DEVELOPER_TOKEN,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ query })
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error?.message || 'Failed to get geo target constants');
      }

      const data = await response.json();
      
      return (data.results || []).map((result: any) => ({
        id: result.geoTargetConstant?.id?.toString() || '',
        name: result.geoTargetConstant?.name || '',
        canonicalName: result.geoTargetConstant?.canonicalName || '',
        countryCode: result.geoTargetConstant?.countryCode || '',
        targetType: result.geoTargetConstant?.targetType || ''
      }));
    } catch (error) {
      console.error('Error getting geo target constants by IDs:', error);
      throw error;
    }
  },

  // ============================================================================
  // CAMPAIGN DRAFTS & EXPERIMENTS API
  // ============================================================================
  
  /**
   * Create Campaign Draft for A/B Testing
   * 
   * Campaign drafts allow you to test changes to a campaign before applying them.
   * You can create a draft, make changes to it, and then either apply those changes
   * to the base campaign or run an experiment to compare performance.
   * 
   * API v22: Uses CampaignDraftService
   * See: https://developers.google.com/google-ads/api/docs/campaign-drafts
   * 
   * @returns Draft resource name
   */
  async createCampaignDraft(
    orgId: string,
    customerId: string,
    baseCampaignResourceName: string,
    draftName: string
  ): Promise<string> {
    try {
      const response = await makeGoogleAdsRequest(
        orgId,
        customerId,
        'campaignDrafts:mutate',
        {
          operations: [{
            create: {
              base_campaign: baseCampaignResourceName,
              name: draftName
            }
          }]
        }
      );

      const draftResourceName = response.results[0].resource_name;
      console.log(`✅ Campaign draft created: ${draftResourceName}`);
      return draftResourceName;
    } catch (error) {
      console.error('Error creating campaign draft:', error);
      throw error;
    }
  },

  /**
   * Get Campaign Draft Details
   */
  async getCampaignDraft(
    orgId: string,
    customerId: string,
    draftResourceName: string
  ): Promise<any> {
    try {
      const cleanCustomerId = customerId.replace(/-/g, '');
      const accessToken = await googleAdsAuth.getAccessToken(orgId);
      
      const query = `
        SELECT
          campaign_draft.resource_name,
          campaign_draft.name,
          campaign_draft.draft_id,
          campaign_draft.base_campaign,
          campaign_draft.draft_campaign,
          campaign_draft.status
        FROM campaign_draft
        WHERE campaign_draft.resource_name = '${draftResourceName}'
      `;

      const response = await fetch(
        `${GOOGLE_ADS_API_BASE}/customers/${cleanCustomerId}/googleAds:search`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'developer-token': DEVELOPER_TOKEN,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ query })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to get campaign draft');
      }

      const data = await response.json();
      return data.results?.[0]?.campaignDraft || null;
    } catch (error) {
      console.error('Error getting campaign draft:', error);
      throw error;
    }
  },

  /**
   * Promote Campaign Draft
   * Applies all changes from the draft to the base campaign
   */
  async promoteCampaignDraft(
    orgId: string,
    customerId: string,
    draftResourceName: string
  ): Promise<void> {
    try {
      await makeGoogleAdsRequest(
        orgId,
        customerId,
        'campaignDrafts:mutate',
        {
          operations: [{
            update: {
              resource_name: draftResourceName,
              status: 'PROMOTED'
            },
            update_mask: { paths: ['status'] }
          }]
        }
      );

      console.log(`✅ Campaign draft promoted: ${draftResourceName}`);
    } catch (error) {
      console.error('Error promoting campaign draft:', error);
      throw error;
    }
  },

  /**
   * Remove Campaign Draft
   */
  async removeCampaignDraft(
    orgId: string,
    customerId: string,
    draftResourceName: string
  ): Promise<void> {
    try {
      await makeGoogleAdsRequest(
        orgId,
        customerId,
        'campaignDrafts:mutate',
        {
          operations: [{
            remove: draftResourceName
          }]
        }
      );

      console.log(`✅ Campaign draft removed: ${draftResourceName}`);
    } catch (error) {
      console.error('Error removing campaign draft:', error);
      throw error;
    }
  },

  /**
   * Create Experiment from Campaign Draft
   * 
   * Creates an A/B test experiment that splits traffic between
   * the base campaign and the draft campaign.
   * 
   * @param trafficSplitPercent Percentage of traffic to send to experiment arm (1-99)
   */
  async createExperiment(
    orgId: string,
    customerId: string,
    draftResourceName: string,
    experimentName: string,
    trafficSplitPercent: number = 50,
    startDate: Date,
    endDate: Date
  ): Promise<string> {
    try {
      // First get the draft to find the draft campaign
      const draft = await this.getCampaignDraft(orgId, customerId, draftResourceName);
      if (!draft || !draft.draftCampaign) {
        throw new Error('Draft campaign not found');
      }

      const response = await makeGoogleAdsRequest(
        orgId,
        customerId,
        'experiments:mutate',
        {
          operations: [{
            create: {
              name: experimentName,
              type: 'SEARCH_CUSTOM',
              status: 'SETUP',
              start_date: formatDateForAPI(startDate),
              end_date: formatDateForAPI(endDate),
              experiment_arms: [
                {
                  control: true,
                  campaigns: [draft.baseCampaign],
                  traffic_split: 100 - trafficSplitPercent
                },
                {
                  control: false,
                  campaigns: [draft.draftCampaign],
                  traffic_split: trafficSplitPercent
                }
              ]
            }
          }]
        }
      );

      const experimentResourceName = response.results[0].resource_name;
      console.log(`✅ Experiment created: ${experimentResourceName}`);
      return experimentResourceName;
    } catch (error) {
      console.error('Error creating experiment:', error);
      throw error;
    }
  },

  /**
   * Start Experiment
   * Changes experiment status from SETUP to ENABLED
   */
  async startExperiment(
    orgId: string,
    customerId: string,
    experimentResourceName: string
  ): Promise<void> {
    try {
      await makeGoogleAdsRequest(
        orgId,
        customerId,
        'experiments:mutate',
        {
          operations: [{
            update: {
              resource_name: experimentResourceName,
              status: 'ENABLED'
            },
            update_mask: { paths: ['status'] }
          }]
        }
      );

      console.log(`✅ Experiment started: ${experimentResourceName}`);
    } catch (error) {
      console.error('Error starting experiment:', error);
      throw error;
    }
  },

  /**
   * End Experiment
   */
  async endExperiment(
    orgId: string,
    customerId: string,
    experimentResourceName: string,
    applyWinningArm: boolean = false
  ): Promise<void> {
    try {
      await makeGoogleAdsRequest(
        orgId,
        customerId,
        'experiments:mutate',
        {
          operations: [{
            update: {
              resource_name: experimentResourceName,
              status: 'ENDED'
            },
            update_mask: { paths: ['status'] }
          }]
        }
      );

      console.log(`✅ Experiment ended: ${experimentResourceName}`);
      
      // Optionally apply winning arm changes
      if (applyWinningArm) {
        console.log('ℹ️ Note: To apply winning arm changes, promote the associated campaign draft');
      }
    } catch (error) {
      console.error('Error ending experiment:', error);
      throw error;
    }
  }
};

